/**
 * 기사 / 장비 관리 모달 ($j.F.Admin.Member.CarDrivers) — 관리자 회원편집 > 기사/장비 > [☰].
 *
 * 소유자 하나의 **기사와 장비를 한 화면에서** 다룬다. 구 `F/Admin/MemberCars` 를 대체한다.
 *
 * [왜 바꿨나]
 *   구 모달은 '지금 편집중인 기사' 중심이라 연결이 항상 그 사람에게만 걸렸다
 *   (`mb_no: MEMBER['num']` 고정). 다른 기사에게 태우려면 그 기사 편집창을 다시 열어야 했고,
 *   장비를 판 경우엔 소속자를 탈퇴시키는 것 말고 방법이 없었다.
 *
 * [좌: 목록 · 우: 본문]
 *   왼쪽은 이 소유자가 가진 것(기사·장비) 전부. 오른쪽은 **한 번에 하나**를 보여준다 —
 *   칩 셋(연결관리·변경이력·가입비내역)이거나, 왼쪽에서 고른 대상의 편집폼이다.
 *   모달을 겹겹이 띄우던 것을 이 한 칸으로 모았다: 뒤에 뭐가 가려졌는지 세지 않아도 된다.
 *
 * [배정은 세 곳에서 바꾼다 — 판정은 한 곳]
 *   연결관리 표 · 장비정보(담당기사) · 기사정보(배정장비). 어디서 보고 있든 그 자리에서 바꾼다 —
 *   예전엔 연결관리 한 곳뿐이라 기사를 보다가도 표로 건너가 그 장비 줄을 다시 찾아야 했다.
 *   셋 다 `relink` 하나를 거쳐 같은 엔드포인트(connect/disconnect)로 간다.
 *
 * [한 기사 = 한 장비]
 *   `g5_cars` 가 UNIQUE(mb_no) 라 기사당 장비는 1대다. 이미 장비를 가진 기사는 셀렉트에서 뺀다
 *   (서버도 막지만, 고를 수 있게 두면 눌러보고 거절당하는 화면이 된다).
 */
import Util from '../../../Common/Util';

export default class CarDrivers {

    constructor() {
        this.data = { drivers: [], cars: [] };

        // **축이 둘이다.** 섞으면 칩 줄에 소유자 것과 개인 것이 나란히 서서 레벨이 어긋난다.
        //   sel  — 왼쪽에서 고른 대상 (없으면 소유자 축을 본다)
        //   view — 그 축에서 지금 보는 것
        this.sel  = { kind: null, id: 0 };
        this.view = 'link';

        // 왼쪽 목록은 기사·장비를 **바꿔서** 본다. 예전엔 둘을 세로로 쌓았는데, 기사가 스무 명쯤
        // 되는 업체에서 장비를 보려면 한참 내려야 했고 스크롤 중에 무슨 목록인지 놓쳤다.
        this.sideTab = 'driver';
    }

    /**
     * 모달을 연 뒤 호출한다 — `#cdModal` 을 찾는다.
     *
     * 루트를 잡아두고 그 안에서만 찾는다. 모달 엔진은 닫힌 노드를 200ms 뒤에 지우므로,
     * 닫자마자 다시 열면 `#cdModal` 이 잠깐 둘이 된다 — 전역 셀렉터면 사라질 노드에도 그린다.
     */
    /**
     * 기사 / 장비 관리 모달을 연다 (마크업 생성 + initialize).
     *
     * 마크업을 **열 때 만든다.** 셸에 숨겨두면 같은 id 가 v1/v2 양쪽에 있어 어느 노드를 잡는지
     * 화면마다 갈린다. 기사편집(MemberEdit/Actions.carList)과 기사관리 목록의 액션 메뉴가
     * 이 한 곳을 쓴다 — 마크업이 두 벌이 되면 반드시 어긋난다.
     */
    open(opts = {}) {
        $j.Ui.JBModal.html('car-list-modal', `
            <div id="cdModal" class="cd">
                <div class="cd-side">
                    <div class="cd-side__head" id="cdSideTabs"></div>
                    <div class="cd-side__body" id="cdSideList"></div>
                </div>

                <div class="cd-split" id="cdSplit" role="separator" aria-orientation="vertical"
                     tabindex="0" title="드래그해서 좌우 폭 조절 · 더블클릭하면 기본값"
                     aria-label="목록과 상세의 폭 조절"><span></span></div>

                <div class="cd-main">
                    <div class="cd-head" id="cdHead"></div>
                    <div class="cd-main__body" id="cdView"></div>
                </div>
            </div>`, '기사 / 장비 관리',
            // vw 는 --adm-zoom 으로 나눈다 (admin/base/_fontsize.scss) — 글자크기 확대 시
            // 모달이 화면 폭을 넘는다. 폴백 1 이라 보통(m)에서는 90vw 그대로다.
            { width: 'calc(90vw / var(--adm-zoom, 1))' });

        this.initialize(opts);
    }

    initialize(opts = {}) {
        // 대상 회원을 인자로 받는다. 기본값은 종전처럼 페이지 전역 MEMBER(기사편집 창) —
        // 기사관리 목록의 액션 메뉴처럼 MEMBER 가 없는 화면에서도 열려면 넘겨야 한다.
        const M = (typeof MEMBER !== 'undefined') ? MEMBER : {};
        this.owner      = opts.owner ?? M['mem_ownercode'];
        this.driverType = opts.driverType ?? M['mem_driver_type'];
        this.$root   = $('#cdModal').last();
        this.sel     = { kind: null, id: 0 };
        // 열면 하는 일이 대개 배정이다. 한도는 머리줄에 늘 보인다(ownerHead).
        // `opts.view` 로 다른 화면부터 열 수 있다 — 편집창의 [소속자추가] 가 'add' 로 연다.
        this.view    = opts.view || 'link';
        this.sideTab = 'driver';
        this._lc     = {};        // 위치동의 상태 — 회원번호별로 한 번만 가져와 기억한다
        this._newCarFor = Util.int(opts.mbNo);   // 'carnew' 로 열 때 — 이 기사 앞으로 등록한다
        this.bind();
        this.load(() => this.openedForCar());

        // 상담 축을 빌린 채로 모달이 닫히면 편집창 상담 열이 남의 번호를 계속 본다
        $j.Common.EventBus.on('$j.F.Admin.Member.CarDrivers', 'JBModal:close', (e, d) => {
            if (d && d.id !== 'car-list-modal') return;
            this.releaseCounsel();
            $j.Common.EventBus.offNamespace('$j.F.Admin.Member.CarDrivers');
        });
    }

    /**
     * 편집창 [+] 장비 추가로 열렸을 때 — **이미 장비가 있는 기사면 그 장비를 연다.**
     * 새 장비 화면으로 두면 저장이 그 기사의 장비를 찾아 덮어쓴다(`apply` 는 기사로 대상을 찾는다).
     */
    openedForCar() {
        if (this.view !== 'carnew' || !this._newCarFor) return;

        const d = this.data.drivers.find((x) => String(x.num) === String(this._newCarFor));
        if (!d || !d.car_num) return;

        this.sideTab = 'car';
        this.drawSide();
        this.goTarget('car', d.car_num);
    }

    /** 목록을 다시 받고 지금 보고 있는 본문도 다시 그린다 */
    /** @param {Function} [then] 새 목록을 받은 뒤 할 일 (예: 방금 만든 사람 고르기) */
    load(then) {
        $.getJSON(`/adm/ajax/${this.owner}/car-drivers`, (d) => {
            this.data = d || { drivers: [], cars: [] };
            this.drawSide();
            this.drawHead();
            // 상담 칸은 자기 상태(편집중인 줄·검색어·유형필터)를 갖고 있고 이 조회와 무관하다.
            // 다시 그리면 쓰던 것이 날아간다 — 한도만 바꿔도 그렇게 된다.
            if (this.view !== 'counsel') this.drawView();
            if (!this.sel.kind) this.drawCap();   // drawView 뒤 (한도칸이 본문 안이다)

            // 새 목록이 이미 그려진 뒤에 부른다 — 여기서 대상을 바꾸면 그 위에 덮어 그린다
            if (typeof then === 'function') then();
        });
    }

    /* ───────────────── 좌: 목록 ───────────────── */

    drawSide() {
        const d = this.data.drivers.length;
        const c = this.data.cars.filter((x) => !x.ended_at).length;
        const tab = (k, t, n) => `<button type="button" class="cd-chip cd-chip--sm`
            + `${this.sideTab === k ? ' is-on' : ''}" data-side="${k}">${t} ${n}</button>`;

        // [+] 는 보고 있는 쪽에 맞춰 뜻이 바뀐다 — 기사면 소속자 추가, 장비면 장비 추가
        const addTip = this.sideTab === 'driver' ? '소속자 추가' : '장비 추가';

        this.$root.find('#cdSideTabs').html(
            tab('driver', '기사', d) + tab('car', '장비', c)
            + `<span class="cd-side__spacer"></span>`
            + `<button type="button" class="me-btn me-btn--accent me-btn--icon" id="cdSideAdd"`
            + ` data-tip-hover data-tip="${addTip}"><i class="fa-solid fa-plus"></i></button>`
        );
        this.$root.find('#cdSideList').html(
            this.sideTab === 'driver' ? this.driverRows() : this.carRows()
        );
        this.markSelected();
    }

    /** 소유자 축 — 고른 대상이 없을 때 보는 것들 */
    /** 서류 종류 — 서버 `DocService::TYPE_*` 와 같은 값 (1:업체 2:장비 3:기사, 0:미분류) */
    static get DOC_TYPE_COMPANY() { return 1; }

    /** 좌우 폭 저장 키 (.ai/state-conventions.md §2 형식) */
    static get K_SIDE_W() { return 'adm:member:cdSideW'; }

    static get OWNER_VIEWS() {
        return [['link', '연결관리'], ['home', '업체 홈'], ['docs', '서류'],
                ['hist', '변경 이력'], ['addcar', '가입비 내역']];
    }

    /**
     * 대상 축에서 볼 수 있는 것.
     *
     * **소유자 본인에게는 기사정보 폼이 없다.** 그 폼이 담는 것은 소속자에 딸린 값인데
     * 소유자는 기사타입·소속·사업자·회비까지 얽혀 있어 여기서 고칠 값이 아니다 (편집창이 그 자리다).
     * 서류와 상담내역만 본다 — 서류는 업체서류가 소유자 행에 붙어 있어 함께 나온다.
     */
    targetViews() {
        if (this.sel.kind === 'car') return [['info', '장비정보'], ['docs', '서류']];
        if (this.isOwnerSel())       return [['docs', '서류'], ['counsel', '상담내역']];
        return [['info', '기사정보'], ['docs', '서류'], ['counsel', '상담내역']];
    }

    /** 왼쪽에서 고른 것이 소유자 본인인가 */
    isOwnerSel() {
        return this.sel.kind === 'driver' && String(this.sel.id) === String(this.owner);
    }

    /** 왼쪽 목록 전환 — 고른 대상은 건드리지 않는다 (오른쪽은 그대로 둔다) */
    goSide(kind) {
        this.sideTab = kind;
        this.drawSide();
    }

    /**
     * 기사 목록 — 고르면 오른쪽이 그 사람 편집폼이 된다.
     *
     * **소유자 본인이 맨 위에 함께 온다.** 목록 이름이 '소속자' 가 아니라 '기사' 인 이유다 —
     * 사용중 장비 11,026대 중 9,887대(90%)를 소유자가 직접 몰고, 소속자가 아예 없는 1인 업체가
     * 8,941곳이다. 빼면 그 업체들은 이 칸이 통째로 비고, 연결관리에서 소유자에게 장비를
     * 붙일 수도 없다. [소유자] 꼬리표로 구분만 한다.
     */
    driverRows() {
        const list = this.data.drivers;
        if (!list.length) return `<div class="cd-empty">기사가 없습니다.</div>`;

        const row = (d) => {
            const isOwner = String(d.num) === String(this.owner);
            const out     = Number(d.driver_status) === 9;

            // 1줄: 이름 … 지역(오른쪽 끝)  /  2줄: 차번호 · 기종
            // 지역을 오른쪽으로 미는 이유는 이름 길이가 제각각이라 가운데 두면 세로로 안 맞기 때문이다.
            const head = `<span class="cd-item__main">
                    <span class="cd-item__name">${this._esc(d.mem_name) || '-'}</span>
                    ${isOwner ? `<span class="cd-tag">소유자</span>` : ''}
                    ${Number(d.is_owner_auth) === 1 ? `<span class="cd-tag cd-tag--auth" title="소유자권한">소</span>` : ''}
                    ${this.driverBadge(d)}
                    <span class="cd-item__right">
                        ${d._addr ? `<span class="cd-item__addr">${this._esc(d._addr)}</span>` : ''}
                        <span class="cd-meta__day${d._seen ? '' : ' is-never'}">${d._seen ? this._esc(d._loginDate) : '미접속'}</span>
                        <span class="cd-item__mobile cd-mobile" role="button" data-num="${d.num}"
                              data-tip-hover data-tip="모바일 화면 열기 (대리로그인)" aria-label="모바일 화면 열기"><i class="fas fa-mobile-screen"></i></span>
                    </span>
                </span>`;

            const has  = !!d.car_num;
            const plate = has
                ? (this._esc(d.car_platenumber) || `<span class="cd-item__none">번호없음</span>`)
                : `<span class="cd-item__none">미배정</span>`;
            const sub = `<span class="cd-item__car">` + (out
                ? `퇴사 ${this._esc(d.co_exit_date || '')}`
                : plate + (d._car ? ` <span class="cd-carinfo">${d._car}</span>` : '')) + `</span>`;

            // **두 줄로 끝낸다.** 세 줄이면 한 화면에 들어오는 기사가 확 줄어드는데,
            // 이 목록은 훑어서 고르는 자리라 몇 명이 한 번에 보이는지가 값 하나 더 보는 것보다 낫다.
            //   1줄  이름 [꼬리표]            … 지역
            //   2줄  차번호 기종              … 수주·서류·최근접속
            const inner = head
                + `<span class="cd-item__sub">${sub}${this.driverMeta(d)}</span>`;

            // 소유자도 **기사 한 명**으로 고른다 (볼 수 있는 것만 다르다 — targetViews).
            // 예전엔 이 줄이 '업체 전체로 돌아가는 문'이었는데, 사람 이름처럼 생긴 줄이 메뉴 구실을
            // 해서 연결관리를 못 찾았다. 업체 탭은 이제 머리줄에 늘 떠 있다(drawHead).
            return `<button type="button" class="cd-item${isOwner ? ' is-owner' : ''}${out && !isOwner ? ' is-off' : ''}"
                            data-kind="driver" data-id="${d.num}">${inner}</button>`;
        };

        // ⚠ 소유자는 퇴사여도 접지 않는다. 목록의 기준줄이라 늘 맨 위에 있어야 하고,
        //   실제로 `g5_member_drivers.status=9` 가 찍힌 소유자가 있다 (본인을 소속기사로도
        //   등록해둔 1인 업체). 상태로만 가르면 그 업체는 목록이 통째로 접혀 비어 보인다.
        const isOwner = (d) => String(d.num) === String(this.owner);
        const here = list.filter((d) => isOwner(d) || Number(d.driver_status) !== 9);
        const out  = list.filter((d) => !isOwner(d) && Number(d.driver_status) === 9);

        // 퇴사자는 접어둔다 — 지우지 않는 이유는 복구가 여기서만 되기 때문이지만,
        // 늘 펼쳐두면 재직자가 밀려 내려간다. <details> 를 쓰는 것은 여닫기 상태를
        // 브라우저가 갖게 해서 이 모듈이 상태를 하나 덜 들고 있기 위해서다.
        let html = here.map(row).join('');
        if (out.length) {
            html += `<details class="cd-fold">
                <summary class="cd-fold__sum">
                    <span class="cd-fold__open">퇴사자 ${out.length}명 더보기</span>
                    <span class="cd-fold__close">퇴사자 접기</span>
                </summary>
                ${out.map(row).join('')}
            </details>`;
        }
        return html;
    }

    /**
     * 목록 둘째 줄 오른쪽에 붙는 값 — 수주(이달)·서류. (최근접속은 첫줄 지역 옆이다)
     *
     * **숫자만 두지 않는다.** 목록(/adm/members)은 머리글이 뜻을 말해주지만 여기는 표가 아니라
     * 뱃지가 나란히 설 뿐이라, 이름표가 없으면 2/5 가 서류인지 수주인지 읽는 사람이 못 가린다.
     * ('이달수주' 로 적었다가 '수주' 로 줄였다 — 한 줄에 함께 서느라 자리가 없고, 이달/전달
     *  구분은 이 기사를 고르면 오른쪽 '정보' 카드가 축까지 밝혀 말한다.)
     *
     * 색 판정은 목록의 렌더러(`F/Admin/Member/ListTable`)와 같게 맞춘다 — 두 화면이 같은 회원을
     * 다르게 말하면 어느 쪽도 못 믿는다: 수주 0 은 회색, 1 이상은 초록 / 서류는 필수개수에 모자라면 강조.
     * 앱버전은 고를 때 보면 되는 값이라 여기 두지 않고 오른쪽 상태카드로 뺐다.
     */
    driverMeta(d) {
        const soo  = Number(d._soo) || 0;
        const lack = Number(d._doc_cnt) !== Number(d._doc_max);

        const cnt = (label, val, tone) =>
            `<span class="cd-kv">${label}<span class="cd-cnt cd-cnt--${tone}">${val}</span></span>`;

        return `<span class="cd-item__meta">
            ${cnt('수주', soo, soo > 0 ? 'green' : 'gray')}
            ${cnt('서류', `${d._doc_cnt}/${d._doc_max}`, lack ? 'noti' : 'gray')}
        </span>`;
    }

    /**
     * 앱버전 — 편집창(열3 '상태 > 정보')과 **같은 문구·같은 배지**.
     * 판정(`latest | old | unsupported`)은 서버가 `AppVersionService::state` 로 미리 내려준다 —
     * 화면에서 다시 재면 두 화면이 갈린다.
     */
    verHtml(d) {
        const ver = d.mobie_ver;
        if (ver === null || ver === undefined || ver === '') return '';

        const latest = this._esc(d._appLatest);
        const badge  = {
            latest:      ['muted',  '최신',        `현재 배포버전(${latest})과 같습니다.`],
            old:         ['warn',   '구버전',      `현재 배포버전은 ${latest} 입니다. 앱을 업데이트하면 해결되는 증상(푸시 미수신 등)이 많습니다.`],
            unsupported: ['danger', '업데이트 필요', `지원 하한 버전보다 낮습니다. 정상 동작을 보장하지 않습니다. (현재 배포 ${latest})`],
        }[d._appState];

        return this._esc(ver)
            + (badge ? ` <span class="me-badge me-badge--${badge[0]}" title="${this._esc(badge[2])}">${badge[1]}</span>` : '');
    }

    /**
     * 소속상태 — `g5_member_drivers.status`. 소유자 본인은 이 테이블에 행이 없는 게 정상이라
     * 값이 없으면 배지를 그리지 않는다 (없음을 '문제'로 보이게 하지 않는다).
     * 공개(1)도 말하지 않는다 — 정상은 배지를 달 이유가 없다.
     */
    driverBadge(d) {
        // ⚠ `Number(null)` 은 0 이다. 가드를 빼면 행이 없는 소유자 본인이 0(예비)으로 잡혀
        //    멀쩡한 사장님에게 '예비' 가 붙는다. 값의 유무를 먼저 가른다.
        if (d.driver_status === null || d.driver_status === undefined || d.driver_status === '') return '';

        const map = { 0: ['예비', 'warn'], 2: ['심사중', 'warn'], 9: ['퇴사', 'danger'] };
        const hit = map[Number(d.driver_status)];
        return hit ? `<span class="cd-badge cd-badge--${hit[1]}">${hit[0]}</span>` : '';
    }

    /**
     * 매각·삭제·복구 버튼의 도움말. **두 곳에 같은 버튼이 있다** — 연결관리 표(`linkView`)와
     * 장비정보 폼(`carView`). 문구를 각자 적으면 반드시 갈리므로 여기 한 벌만 둔다.
     *
     * 문구는 **누르면 무슨 일이 벌어지는가**로 쓴다. 지금 이 장비가 어떤 상태인지는 목록의
     * 배지(매각/삭제)가 이미 말하고 있어, 툴팁까지 상태를 설명하면 같은 말을 두 번 하면서
     * 정작 버튼이 무엇을 하는지는 안 알려준다.
     *
     * 매각과 삭제는 **벌어지는 일이 같다** (`CarService::retire` 하나가 처리한다 — 기사 연결을
     * 끊고 `ended_at` 을 찍는다). 다른 것은 `car_status` 에 남는 **사유**뿐이다.
     */
    static tip(kind) {
        const T = {
            sell:    ['매각', '이 장비를 매각 처리합니다. 기사 연결이 풀리고 매칭에서 빠지며 한도 한 자리가 빕니다. 되돌리려면 [복구]를 누릅니다.'],
            delete:  ['삭제', '이 장비를 삭제 처리합니다. 벌어지는 일은 매각과 같고 사유만 \'삭제\'로 남습니다. 실제로 지워지지는 않아 나중에 복구할 수 있습니다.'],
            restore: ['복구', '이 장비를 다시 사용중으로 되돌립니다. 매칭에 다시 잡히고 한도 한 자리를 씁니다. 기사는 붙지 않으니 연결관리에서 다시 배정하세요.'],
        }[kind];
        return ` data-tip-hover data-tip-title="${T[0]}" data-tip="${T[1]}"`;
    }

    /** 장비 목록 — 사용중이 위, 매각·삭제가 아래 */
    carRows() {
        const list = this.data.cars;
        if (!list.length) return `<div class="cd-empty">등록된 장비가 없습니다.</div>`;

        const row = (c) => {
            const off  = !!c.ended_at;
            const kind = c.car_status === 'SOLD' ? '매각' : '삭제';
            return `<button type="button" class="cd-item${off ? ' is-off' : ''}"
                            data-kind="car" data-id="${c.num}">
                <span class="cd-item__main">
                    <span class="cd-item__name">${this._esc(c.car_platenumber) || '번호없음'}</span>
                    ${off ? `<span class="cd-badge cd-badge--danger">${kind}</span>` : ''}
                    <span class="cd-item__right">
                        ${c.driver_name
                            ? `<span class="cd-item__who"><i class="fa-solid fa-user"></i> ${this._esc(c.driver_name)}</span>`
                            : `<span class="cd-item__none">미배정</span>`}
                    </span>
                </span>
                <span class="cd-item__sub">
                    <span class="cd-item__car cd-carinfo">${c._car || this._esc(c.car_type) || '-'}</span>
                    <span class="cd-item__meta">
                        <span class="cd-kv">서류<span class="cd-cnt cd-cnt--${Number(c._docCnt) ? 'gray' : 'noti'}">${Number(c._docCnt) || 0}</span></span>
                    </span>
                </span>
            </button>`;
        };

        const live    = list.filter((c) => !c.ended_at);
        const retired = list.filter((c) => c.ended_at);

        // 매각·삭제도 퇴사자와 같은 방식으로 접는다 — 지우지 않는 이유는 복구가 여기서만
        // 되기 때문이고, 접는 이유는 늘 펼쳐두면 쓰는 장비가 밀려 내려가기 때문이다.
        // 두 목록이 같은 규약을 쓰므로 한쪽만 보고도 다른 쪽을 안다.
        let html = live.map(row).join('');
        if (retired.length) {
            html += `<details class="cd-fold">
                <summary class="cd-fold__sum">
                    <span class="cd-fold__open">매각 · 삭제 ${retired.length}대 더보기</span>
                    <span class="cd-fold__close">매각 · 삭제 접기</span>
                </summary>
                ${retired.map(row).join('')}
            </details>`;
        }
        return html;
    }

    markSelected() {
        this.$root.find('.cd-item[data-kind]').each((_, el) => {
            const $el = $(el);
            $el.toggleClass('is-on',
                this.sel.kind === $el.data('kind') && String(this.sel.id) === String($el.data('id')));
        });
    }

    /** 최대 장비대수 — 바꾸는 자리는 여기 하나다 */
    drawCap() {
        const cap  = this.data.cap || { max: 0, used: 0 };
        const full = cap.used >= cap.max;
        const left = Math.max(0, cap.max - cap.used);

        // 업체 홈을 보고 있을 때만 자리가 있다 (다른 칩을 눌렀으면 비어 있는 게 정상)
        this.$root.find('#cdCap').html(
            `<span class="cd-cap__now${full ? ' is-full' : ''}">사용중 <b>${cap.used}</b></span>`
            + `<span class="cd-cap__sep">/</span>`
            + `<span class="cd-cap__max">최대`
            + ` <input type="number" id="cdMax" value="${cap.max}" min="0" max="99"></span>`
            + `<button type="button" class="me-btn" id="cdMaxSave">변경</button>`
            + `<span class="cd-cap__left">${full ? '빈 자리 없음' : `${left}대 더 넣을 수 있음`}</span>`
        );

        // 본창 '기사 / 장비 ( 최대 N 대 )' 도 맞춰준다 — 모달을 닫고 나서 옛 숫자가 남아 있으면
        // 어느 쪽이 맞는지 알 수 없다.
        $('#meCarMax').text(cap.max);
    }

    /* ───────────────── 우: 본문 ───────────────── */

    /**
     * 오른쪽 머리 — **업체 탭은 늘 떠 있고, 대상을 고르면 그 아래 한 줄이 더 선다.**
     *
     * 예전엔 대상을 고르면 머리줄을 통째로 갈아끼웠다. 그러면 업체 탭(연결관리 등)이 사라져서
     * 기사 하나를 보다가 연결관리로 가려면 왼쪽 소유자 줄을 눌러 '업체 전체'로 돌아와야 했는데,
     * 그 줄이 돌아가는 문이라는 걸 알아볼 방법이 없었다 (장비 탭에는 그 줄조차 없다).
     * 지금은 어디서든 업체 탭을 한 번에 누른다 — 누르면 고른 대상을 놓는다(goOwner).
     *
     * 둘을 **줄로 가른다.** 한 줄에 나란히 두면 '앞은 업체 전체, 뒤는 한 사람'이 읽히지 않는다.
     */
    drawHead() {
        // ⚠ 칩에 **축(owner/target)을 함께 박는다.** `docs`(서류)는 양쪽 줄에 다 있는 이름이라
        //   이름만으로는 어느 줄의 것인지 알 수 없다 — 켜짐 판정도, 클릭 핸들러도 축을 본다.
        const hasSel = !!this.sel.kind;
        const chip = (axis) => (v, t) => {
            const on = this.view === v && (axis === 'target') === hasSel;
            return `<button type="button" class="cd-chip${on ? ' is-on' : ''}"`
                + ` data-view="${v}" data-axis="${axis}">${t}</button>`;
        };

        const sub = hasSel ? this.targetHead(chip('target')) : this.stepHead();
        this.$root.find('#cdHead').html(this.ownerHead(chip('owner')) + sub)
            .toggleClass('is-target', hasSel);

        // 본창 '기사 / 장비 ( 최대 N 대 )' 도 맞춘다 — 한도는 업체 홈 말고도 바뀐다(소속자 추가·새 장비)
        if (this.data.cap) $('#meCarMax').text(this.data.cap.max);
    }

    /** 업체 전체 — 늘 첫 줄이다 */
    ownerHead(chip) {
        return `<div class="cd-head__top">
                    <span class="cd-head__title">업체 전체</span>
                    ${this.capChip()}
                    <span class="cd-head__spacer"></span>
                    <span class="cd-head__tabs">
                        ${CarDrivers.OWNER_VIEWS.map(([v, t]) => chip(v, t)).join('')}
                    </span>
                </div>`;
    }

    /**
     * 한도 — 어느 화면에서든 보인다. 장비를 못 넣는 이유가 대개 이것이라 첫 화면이 무엇이든
     * 눈에 들어와야 한다. 누르면 바꾸는 자리(업체 홈)로 간다.
     */
    capChip() {
        const cap  = this.data.cap || { max: 0, used: 0 };
        const full = cap.used >= cap.max;

        return `<button type="button" class="cd-head__cap${full ? ' is-full' : ''}" id="cdHeadCap"
                        data-tip-hover data-tip="최대 장비대수 — 누르면 업체 홈에서 바꿉니다">
                    사용중 <b>${cap.used}</b> / 최대 <b>${cap.max}</b></button>`;
    }

    /** 탭에 없는 화면(소속자 추가·새 장비) — 켜진 탭이 없으므로 지금 어디인지 둘째 줄에 적는다 */
    stepHead() {
        const t = { add:    ['fa-user-plus', '새 소속자'],
                    carnew: ['fa-truck', '새 장비' + (this._newCarFor ? ` — ${this.driverName(this._newCarFor)}` : '')] }[this.view];
        if (!t) return '';

        return `<div class="cd-head__top">
                    <span class="cd-head__title"><i class="fa-solid ${t[0]}"></i> ${this._esc(t[1])}</span>
                </div>`;
    }

    /**
     * 고른 대상 하나 — 둘째 줄. 이름을 박아 지금 누구 이야기인지 남긴다: 이름 … 칩 · 편집창.
     *
     * - 이름 옆 '기사'/'장비' 꼬리표는 없다. 아이콘(사람/트럭)이 이미 종류를 말한다.
     * - 편집창 열기는 아이콘 하나다 — 자주 쓰는 버튼이 아닌데 글자로 두면
     *   칩과 같은 무게로 서서 무엇이 화면 전환인지 흐려진다.
     */
    targetHead(chip) {
        const isDrv = this.sel.kind === 'driver';
        const who   = isDrv ? this.driverName(this.sel.id) : this.carName(this.sel.id);

        return `<div class="cd-head__top">
                    <span class="cd-head__title">
                        <i class="fa-solid ${isDrv ? 'fa-user' : 'fa-truck'}"></i>
                        ${this._esc(who)}
                    </span>
                    <span class="cd-head__spacer"></span>
                    <span class="cd-head__tabs">
                        ${this.targetViews().map(([v, t]) => chip(v, t)).join('')}
                    </span>
                    ${isDrv ? `<button type="button" class="me-btn me-btn--icon cd-mobile" data-num="${this.sel.id}"
                                       data-tip-hover data-tip="이 기사의 모바일 화면을 엽니다 (대리로그인)"
                                       aria-label="모바일 화면 열기"><i class="fas fa-mobile-screen"></i></button>` : ''}
                    ${isDrv ? `<button type="button" class="me-btn me-btn--icon cd-goto" data-num="${this.sel.id}"
                                       data-tip-hover data-tip="이 기사의 편집창을 새 창으로 엽니다"
                                       aria-label="편집창 열기"><i class="fa-solid fa-up-right-from-square"></i></button>` : ''}
                </div>`;
    }

    /** 소유자 축으로 — 고른 대상을 놓는다 */
    goOwner(view) {
        this.leaveCounsel(view);
        this.sel  = { kind: null, id: 0 };
        this.view = view;
        this.after();
    }

    /**
     * 대상을 고른다 — **보던 칩을 그대로 유지한다.**
     *
     * 서류를 보다가 옆 기사를 누르면 그 기사의 서류가 나와야 한다. 매번 첫 칩으로 되돌리면
     * 같은 것을 여러 명에게서 볼 때마다 칩을 다시 눌러야 했다.
     *
     * 다만 축이 바뀌면 없는 칩이 생긴다 — 기사에는 상담내역이 있고 장비에는 없다.
     * 그럴 때만 그 대상의 첫 칩으로 떨어진다 (판정은 `targetViews` 한 곳).
     */
    goTarget(kind, id) {
        this.sel = { kind, id };

        const allowed = this.targetViews().map(([v]) => v);
        const next    = allowed.includes(this.view) ? this.view : allowed[0];

        this.leaveCounsel(next);
        this.view = next;
        this.after();
    }

    /**
     * 고른 대상 안에서 탭만 바꾼다.
     *
     * **그 대상이 가진 탭인지 확인한다.** 화면에 없는 탭은 눌릴 일이 없지만, 이 규칙이
     * `targetViews()` 한 곳에만 있으면 나중에 다른 경로가 생겼을 때 소유자에게 편집폼이
     * 뜬다 — 표시와 판정을 같은 함수에 묶어둔다.
     */
    goTargetView(view) {
        const allowed = this.targetViews().map(([v]) => v);
        if (!allowed.includes(view)) view = allowed[0];

        this.leaveCounsel(view);
        this.view = view;
        this.after();
    }

    /** 상담을 벗어나면 축을 되돌린다 (남기면 편집창 상담 열이 남의 번호를 본다) */
    leaveCounsel(next) {
        if (this.view === 'counsel' && next !== 'counsel') this.releaseCounsel();
    }

    after() {
        this.drawHead();
        this.markSelected();
        this.drawView();
        // ⚠ 반드시 drawView 뒤다 — 한도칸(`#cdCap`)은 업체 홈 본문 안에 있어서
        //   그리기 전에 채우면 넣을 자리가 없다.
        if (!this.sel.kind) this.drawCap();
    }

    drawView() {
        const $v = this.$root.find('#cdView');
        switch (this.view) {
            case 'home':    return $v.html(this.homeView());
            case 'link':    return $v.html(this.linkView());
            case 'hist':    return this.remoteView($v, `/adm/ajax/${this.owner}/car/logs`, (d) => this.histHtml(d));
            case 'addcar':  return this.remoteView($v, `/adm/ajax/${this.owner}/car/addcar-log`, (d) => this.logHtml(d));
            case 'counsel': return this.counselView($v);
            case 'info':    return this.sel.kind === 'driver' ? this.showDriver($v) : this.carView($v);
            case 'add':     return this.addDriverView($v);
            case 'carnew':  return this.newCarView($v);
            case 'docs':    return this.docsView($v);
        }
    }

    /* ── 소속자 추가 ── */

    /**
     * 새 소속자 입력폼. 저장은 편집과 **같은 엔드포인트**(`/adm/ajax/member/v2/save`)로 가고
     * `editMode=add` 만 다르다 — 채번·중복검사·가입처리(MemberSignupService)·GPS 승계가
     * 전부 그쪽에 있어서, 여기서 다시 만들면 두 벌이 된다.
     */
    addDriverView($v) {
        const field = (label, ctrl, req) =>
            `<div class="me-field">
                <span class="me-field__label">${label}${req ? '<b class="cd-req">*</b>' : ''}</span>
                <span class="me-field__ctrl">${ctrl}</span>
            </div>`;

        $v.html(`<form class="cd-form" id="cdAddForm" onsubmit="return false">
            <div class="cd-form__cols cd-form__cols--one">
                <div class="me-col">
                    <section class="me-block"><div class="me-block__head">새 소속자</div>
                        <div class="me-block__body">
                            ${field('이름', `<input type="text" class="me-input" name="mem_name" maxlength="30" autocomplete="off">`, true)}
                            ${field('핸드폰', `<input type="tel" class="me-input" id="cdAddPhone" name="mem_phone" maxlength="15" autocomplete="off">`, true)}
                            ${field('비밀번호', `<input type="password" class="me-input" name="mem_pwd" autocomplete="new-password" placeholder="4자 이상">`, true)}
                            ${field('생년월일', `<input type="text" class="me-input is-date js-date" name="mem_birthday" maxlength="10" placeholder="YYYY-MM-DD" autocomplete="off">`)}
                            ${field('아이디', `<input type="text" class="me-input" name="mem_id" id="cdAddId" readonly value="채번중…">`
                                + `<span class="me-field__hint">소유자 핸드폰 + 순번</span>`)}
                        </div>
                    </section>
                    ${this.addCarSection()}
                </div>
            </div>
            <div class="cd-form__act">
                <button type="button" class="me-save" id="cdAddSave">등록</button>
                <button type="button" class="me-btn" id="cdAddCancel">취소</button>
                <span class="cd-note cd-note--inline" id="cdAddNote">등록하면 왼쪽 목록에 바로 붙습니다.
                    서류·제재·기사타입은 편집창에서 다룹니다.</span>
                <span class="cd-note cd-note--inline cd-note--short" id="cdAddShort"></span>
            </div>
        </form>`);

        this.syncAddCar();

        if ($j.p && $j.p._initDatePickers) $j.p._initDatePickers($v.find('.js-date'));
        $j.Common.Form.registerPhoneHandler('cdAddPhone');   // 하이픈 자동

        // 아이디는 서버가 채번한다 — 화면에서 만들면 이미 쓰는 순번과 부딪힌다
        $.getJSON(`/adm/ajax/${this.owner}/next-emp-id`, (r) => {
            $v.find('#cdAddId').val(r && r.result ? r.mem_id : '');
            // 이름 기본값(사장이름+순번) — 그 사이 이미 쳤으면 덮지 않는다
            const $name = $v.find('[name="mem_name"]');
            if (r && r.mem_name && !$name.val()) $name.val(r.mem_name);
            if (!(r && r.result)) $j.F.Admin.Util.tsalert('error', r && r.message || '아이디를 만들지 못했습니다.');
        });
    }

    /**
     * 소속자와 함께 정할 장비 — **장비가 늘어나는 일을 먼저 묻는다.**
     *
     * 소속자를 넣는 일은 대개 장비 한 대가 느는 일이다: 한도를 늘리고 → 추가등록비를 빼고 →
     * 장비를 등록하고 → 그 기사에게 붙인다. 넷이 화면 네 곳에 흩어져 있어 하나를 빠뜨리기 쉬웠다
     * (특히 돈). 그래서 등록 전에 한도·등록비를 여기서 정하고, 등록하면 장비 입력으로 바로 넘긴다.
     *
     * 발주자 업체는 장비가 없으므로 이 칸을 그리지 않는다.
     */
    addCarSection() {
        if (!this.isSoojooOwner()) return '';

        const free = this.data.cars.filter((c) => !c.ended_at && !Util.int(c.mb_no));

        // 소속자를 넣는 일은 대개 장비가 한 대 느는 일이다 — 미배정 장비가 있어도 새 장비가 기본이다
        const def = 'new';
        const opt = (v, t) => `<label><input type="radio" name="cdAddCar" value="${v}"`
            + `${def === v ? ' checked' : ''}> ${t}</label>`;

        const capBox = this.raiseBox();

        const linkBox = free.length
            ? `<div class="cd-opt__sub" data-for="link">
                   <select class="me-select" id="cdAddLinkCar">${free.map((c) =>
                       `<option value="${c.num}">${this._esc(c.car_platenumber) || '번호없음'}`
                       + ` [${this._esc(c.car_type) || '-'}] ${this._esc(c.car_str)}</option>`).join('')}</select>
               </div>` : '';

        return `<section class="me-block"><div class="me-block__head">장비</div>
            <div class="me-block__body cd-opt">
                <div class="me-radios cd-opt__radios">
                    ${opt('new', '새 장비를 등록해 연결')}
                    ${free.length ? opt('link', '미배정 장비에 연결') : ''}
                    ${opt('none', '장비 없이 소속자만')}
                </div>
                <div class="cd-opt__sub" data-for="new">${capBox}</div>
                ${linkBox}
            </div>
        </section>`;
    }

    /** 이 업체가 장비를 갖는 쪽(수발주자)인가 — 소유자 귀속값이라 어느 줄에서 읽어도 같다 */
    isSoojooOwner() {
        const d = this.data.drivers[0];
        return ((d && d.mem_driver_type) || this.driverType) === '수발주자';
    }

    /**
     * 한도·추가등록비 칸 — 장비가 한 대 느는 자리(소속자 추가 · 새 장비)가 함께 쓴다.
     * 빈 자리가 있으면 한 줄 안내뿐이고, 찼으면 한도 +1 과 추가등록비를 여기서 정한다.
     * 읽는 쪽은 `readRaise` — 찼는지를 `data-raise` 로 박아 그린 것과 읽는 것이 어긋나지 않게 한다.
     */
    raiseBox() {
        const cap  = this.data.cap || { max: 0, used: 0 };
        const left = Math.max(0, cap.max - cap.used);

        if (left > 0) {
            return `<div class="cd-raise" data-raise="0" data-max="${cap.max}">
                <div class="cd-opt__line">빈 자리 <b>${left}</b>대 (사용중 ${cap.used} / 최대 ${cap.max})
                    — 한도를 늘리지 않고 등록합니다.</div></div>`;
        }
        return `<div class="cd-raise" data-raise="1" data-max="${cap.max}">
            <div class="cd-opt__line cd-opt__line--warn">한도가 찼습니다 (사용중 ${cap.used} / 최대 ${cap.max}).
                최대 장비대수를 <b>${cap.max}대 → ${cap.max + 1}대</b>로 늘립니다.</div>
            <div class="cd-opt__line">
                <label><input type="checkbox" id="cdAddFeeOn" checked> 추가등록비 차감</label>
                <input type="text" class="me-input cd-opt__fee" id="cdAddFee" inputmode="numeric"
                       value="${Util.formatNumber(Util.int(this.data.addFee))}" data-amount-kor> 원
                <span class="cd-opt__bal" id="cdAddBal"></span>
            </div>
            <div class="cd-opt__line">
                <input type="text" class="me-input" id="cdAddFeeMemo" maxlength="100" autocomplete="off"
                       value="${this._esc(this.addFeeMemo())}" placeholder="캐시내역 메모">
            </div>
        </div>`;
    }

    /**
     * 한도·추가등록비 칸의 값. 빠진 것이 있으면 알리고 null.
     * @returns {{raise:boolean, from:number, fee:number, memo:string}|null}
     */
    readRaise() {
        const $r = this.$root.find('#cdView .cd-raise');
        if (Number($r.data('raise')) !== 1) return { raise: false, from: 0, fee: 0, memo: '' };

        const on   = $r.find('#cdAddFeeOn').prop('checked');
        const fee  = on ? Util.int(String($r.find('#cdAddFee').val() || '').replace(/,/g, '')) : 0;
        const memo = String($r.find('#cdAddFeeMemo').val() || '').trim();

        if (on && fee <= 0) { $j.F.Admin.Util.tsalert('error', '추가등록비 금액을 입력해주세요.'); $r.find('#cdAddFee').focus(); return null; }
        if (on && !memo)    { $j.F.Admin.Util.tsalert('error', '추가등록비 메모를 입력해주세요.'); $r.find('#cdAddFeeMemo').focus(); return null; }

        return { raise: true, from: Util.int($r.data('max')), fee, memo };
    }

    /**
     * 추가등록비 메모 기본문구 — 캐시 입출금(`cashInout`)의 [추가등록비] 프리셋과 같은 문구.
     * 캐시는 소유자에게서 빠지므로 이름도 소유자다. 톤수는 CS 가 채운다.
     */
    addFeeMemo() {
        return `${this.driverName(this.owner)}_추가등록 ( * 톤) - 톤수입력필수`;
    }

    /** 돈이 나가는 일은 한 번 더 보여주고 받는다 */
    confirmRaise(title, hint, { from, fee }) {
        const cash = Util.int(this.data.cash);
        return $j.Ui.Dialog.open({
            tone:  'warn',
            title,
            rows:  [{ k: '최대 장비대수', v: `${from}대 → ${from + 1}대` },
                    { k: '추가등록비', v: fee > 0 ? `${Util.formatNumber(fee)}원 차감` : '차감 없음' },
                    ...(fee > 0 ? [{ k: '가용캐시', v: `${Util.formatNumber(cash)}원 → ${Util.formatNumber(cash - fee)}원` }] : [])],
            hint,
            confirm: '등록', cancel: '취소',
        });
    }

    /**
     * 한도 +1 · 추가등록비 차감을 보낸다. 성공하면 **화면이 들고 있는 한도·캐시도 맞춘다** —
     * 목록을 다시 받기 전에 같은 폼에서 한 번 더 누르면 옛 한도로 또 늘리려 든다.
     */
    async sendRaise({ from, fee, memo }) {
        const r = await this.send(`/adm/ajax/${this.owner}/car/cap-raise`, { from, add: 1, fee, memo });
        if (!r.result) return r;

        this.data.cap  = { ...(this.data.cap || { used: 0 }), max: from + 1 };
        this.data.cash = Util.int(this.data.cash) - fee;
        $j.F.Admin.Util.tsalert('success',
            `최대 장비대수 ${from + 1}대` + (fee > 0 ? ` · 추가등록비 ${Util.formatNumber(fee)}원 차감` : ''), 'bottom-right');
        return r;
    }

    /**
     * 장비 선택에 맞춰 아래 칸을 바꾸고, 차감 뒤 잔액을 다시 적는다 (소속자 추가 · 새 장비 공용).
     * 차감하면 가용캐시가 마이너스가 되는 동안은 등록 버튼을 잠근다 — 면제(체크 해제)·금액 조정·
     * 소속자만 등록처럼 돈이 안 나가는 길은 잠그지 않는다.
     */
    syncAddCar() {
        const $v = this.$root.find('#cdView');

        const mode = $v.find('[name=cdAddCar]:checked').val();
        $v.find('.cd-opt__sub[data-for]').each((_, el) => $(el).toggle($(el).data('for') === mode));

        const $r = $v.find('.cd-raise');
        if (!$r.length) return;

        const on = $r.find('#cdAddFeeOn').prop('checked');
        $r.find('#cdAddFee, #cdAddFeeMemo').prop('disabled', !on);

        const cash = Util.int(this.data.cash);
        const fee  = on ? Util.int(String($r.find('#cdAddFee').val() || '').replace(/,/g, '')) : 0;
        const rest = cash - fee;
        $r.find('#cdAddBal')
            .text(`가용캐시 ${Util.formatNumber(cash)}원 → 차감 후 ${Util.formatNumber(rest)}원`)
            .toggleClass('is-short', rest < 0);

        // 소속자 추가에서 '새 장비' 가 아니면 돈이 안 나간다. 새 장비 화면엔 라디오가 없다(mode 없음).
        const short = (!mode || mode === 'new') && fee > 0 && rest < 0;
        $v.find('#cdAddShort').text(short ? `가용캐시가 ${Util.formatNumber(-rest)}원 부족해 등록할 수 없습니다.` : '');
        $v.find('#cdAddNote').toggle(!short);
        $v.find('#cdAddSave, #cdNewCarSave').prop('disabled', short);
    }

    /**
     * 입금은 별창에서 하므로 모달이 든 잔액은 낡는다. 잠긴 채로 이 창에 돌아오면 잔액만 새로 받아
     * 버튼 상태를 다시 계산한다 — 폼을 다시 그리면 입력이 날아간다.
     */
    onWindowFocus() {
        if (!this.$root.find('#cdAddShort').text()) return;
        $.getJSON(`/adm/ajax/${this.owner}/car-drivers`, (d) => {
            this.data.cash = Util.int(d && d.cash);
            this.syncAddCar();
        });
    }

    /**
     * 새 소속자 등록 — 장비까지 한 줄기로 잇는다.
     *
     *   ① 소속자 등록 → ② (한도가 찼으면) 한도 +1 · 추가등록비 차감 → ③④ 장비 등록 = 그 기사에게 연결
     *
     * **소속자를 먼저 만든다.** 돈을 먼저 빼면 핸드폰 중복 등으로 등록이 거절됐을 때 차감만 남는다.
     * 반대로 ②가 실패하면 소속자는 남고, 한도는 업체 홈에서 다시 올릴 수 있다.
     */
    async onAddSave() {
        const $f = this.$root.find('#cdAddForm');
        const v  = (n) => String($f.find(`[name="${n}"]`).val() || '').trim();

        if (!v('mem_name'))  { $j.F.Admin.Util.tsalert('error', '이름을 입력해주세요.');   $f.find('[name="mem_name"]').focus();  return; }
        if (!v('mem_phone')) { $j.F.Admin.Util.tsalert('error', '핸드폰을 입력해주세요.'); $f.find('[name="mem_phone"]').focus(); return; }
        // 서버는 4자 미만이면 비밀번호를 아예 반영하지 않는다(save 의 php8_str_len > 3).
        // 신규는 못 들어가는 계정이 되므로 여기서 막는다.
        if (v('mem_pwd').length < 4) { $j.F.Admin.Util.tsalert('error', '비밀번호를 4자 이상 입력해주세요.'); $f.find('[name="mem_pwd"]').focus(); return; }
        if (!v('mem_id'))    { $j.F.Admin.Util.tsalert('error', '아이디를 채번하지 못했습니다. 다시 열어주세요.'); return; }

        // 장비를 어떻게 할지 — 칸이 없으면(발주자 업체) 소속자만 만든다
        const mode = $f.find('[name=cdAddCar]:checked').val() || 'none';
        const link = mode === 'link' ? $f.find('#cdAddLinkCar').val() : '';
        const rz   = mode === 'new' ? this.readRaise() : { raise: false };
        if (!rz) return;

        if (rz.raise && !await this.confirmRaise(`${v('mem_name')} 소속자를 등록하고 장비대수를 늘릴까요?`,
                '등록하면 이어서 장비 입력으로 넘어갑니다.', rz)) return;

        const $btn = $f.find('#cdAddSave');
        $btn.prop('disabled', true).text('등록중…');
        const fail = (msg) => {
            $j.F.Admin.Util.tsalert('error', msg);
            $btn.prop('disabled', false).text('등록');
        };

        // ① 소속자
        const d = await this.send('/adm/ajax/member/v2/save', {
            editMode:      'add',
            mem_ownercode: this.owner,
            mem_permit:    '소속자',
            mem_id:        v('mem_id'),
            mem_pwd:       v('mem_pwd'),
            mem_name:      v('mem_name'),
            mem_phone:     v('mem_phone'),
            mem_birthday:  v('mem_birthday'),
            mem_status:    0,
        });
        if (!d.result) return fail(d.message || '등록하지 못했습니다.');

        const mbNo = d.mb_no;
        $j.F.Admin.Util.tsalert('success', d.message || '소속자를 등록했습니다.');

        // ② 한도 +1 · 추가등록비 — 실패해도 소속자는 이미 있다. 그 사람을 열어두고 사유를 알린다.
        if (rz.raise) {
            const r = await this.sendRaise(rz);
            if (!r.result) {
                await $j.Ui.Dialog.alert('소속자는 등록했지만 장비대수를 늘리지 못했습니다.',
                    { desc: r.message || '', hint: '이어지는 장비 입력에서 다시 늘릴 수 있습니다.' });
            }
        }

        // 미배정 장비를 붙이는 경우 — 연결까지 하고 그 사람을 연다
        if (link) {
            const r = await this.send(`/adm/ajax/${this.owner}/car/connect`, { num: link, mb_no: mbNo });
            if (!r.result) await $j.Ui.Dialog.alert(r.message || '장비를 연결하지 못했습니다.');
        }

        if ($j.p && $j.p.loadCar) $j.p.loadCar();   // 본창 장비요약·한도 갱신

        // ③④ 새 장비 — 입력 화면으로 넘긴다. 나머지는 방금 만든 사람을 골라 놓는다.
        this.load(() => (mode === 'new' ? this.goNewCar(mbNo) : this.goTarget('driver', mbNo)));
    }

    /* ── 새 장비 (기사에게 바로 붙인다) ── */

    /**
     * 새 장비 입력으로 — 두 길이 같은 화면을 쓴다.
     * @param {number} [mbNo] 방금 만든 소속자(그 사람에게 붙인다). 없으면 왼쪽 [+] 장비 추가 — 담당기사를 고른다
     */
    goNewCar(mbNo) {
        this.leaveCounsel('carnew');
        this._newCarFor = mbNo || 0;
        this.sel  = { kind: null, id: 0 };
        this.view = 'carnew';
        this.after();
    }

    /**
     * 새 장비 입력 — 저장하면 **등록과 연결이 한 번에** 된다 (`apply` 에 `mb_no` 를 실어보낸다).
     * 자리가 찼으면 한도 +1 · 추가등록비도 여기서 정한다(`raiseBox`) — 장비가 느는 길은 전부 이 화면을 지난다.
     * 폼은 장비편집(`carView`)과 같은 `$j.F.CarSelect` 를 쓴다.
     */
    newCarView($v) {
        const mbNo = this._newCarFor;
        const field = (label, ctrl, stack) =>
            `<div class="me-field${stack ? ' me-field--stack' : ''}">
                <span class="me-field__label">${label}</span>
                <span class="me-field__ctrl">${ctrl}</span>
            </div>`;

        // 담당기사 — 소속자 추가에서 넘어왔으면 그 사람으로 정해져 있다. 아니면 장비 없는 기사 중에 고른다.
        const who = mbNo
            ? `<span class="me-ro">${this._esc(this.driverName(mbNo))}</span>`
            : `<select class="me-select" id="cdNewCarDriver"><option value="">— 미배정 —</option>`
              + this.data.drivers
                  .filter((d) => !d.car_num && Number(d.driver_status) !== 9 && Number(d.mem_status) === 0)
                  .map((d) => `<option value="${d.num}">${this._esc(d.mem_name) || d.num}</option>`).join('')
              + `</select><span class="me-field__hint">비워두면 장비만 만듭니다</span>`;

        $v.html(`<div class="cd-form" id="cdNewCarForm" data-mb="${mbNo}">
            <section class="me-block">
                <div class="me-block__head">새 장비</div>
                <div class="me-block__body">
                    ${field('기종',
                        `<select id="cd-cartype" class="me-select"></select>`
                        + `<select id="cd-depth1"  class="me-select"></select>`
                        + `<select id="cd-depth2"  class="me-select"></select>`
                        + `<select id="cd-jungo"   class="me-select"></select>`, true)}
                    ${field('차량번호',
                        `<input id="cd-number" class="me-input" type="text" autocomplete="off" placeholder="차량번호 입력">`)}
                    ${field('옵션', `<span id="cd-option"></span>`, true)}
                    ${field('담당기사', who)}
                </div>
            </section>
            <section class="me-block">
                <div class="me-block__head">최대 장비대수</div>
                <div class="me-block__body cd-opt">${this.raiseBox()}</div>
            </section>
            <div class="cd-form__act">
                <button type="button" class="me-save" id="cdNewCarSave">장비 등록</button>
                <button type="button" class="me-btn" id="cdNewCarSkip">${mbNo ? '나중에' : '취소'}</button>
                <span class="cd-note cd-note--inline cd-note--short" id="cdAddShort"></span>
            </div>
        </div>`);

        $j.F.CarSelect.initialize({
            idList: { car_type: 'cd-cartype', depth1: 'cd-depth1', depth2: 'cd-depth2',
                      jungo: 'cd-jungo', number: 'cd-number', option: 'cd-option' },
            v: {},
        });
        this.syncAddCar();
    }

    async onNewCarSave() {
        const $f   = this.$root.find('#cdNewCarForm');
        const mbNo = Util.int($f.data('mb')) || Util.int($f.find('#cdNewCarDriver').val());
        const data = this.carFormData(mbNo);
        if (!data) return;

        // 자리가 찼으면 한도부터 — 장비 등록이 한도를 본다
        const rz = this.readRaise();
        if (!rz) return;
        if (rz.raise) {
            if (!await this.confirmRaise('장비대수를 늘리고 장비를 등록할까요?', '', rz)) return;

            const r = await this.sendRaise(rz);
            if (!r.result) { await $j.Ui.Dialog.alert(r.message || '장비대수를 늘리지 못했습니다.'); return this.load(); }

            // 늘린 뒤 장비 등록이 거절되면(번호 중복 등) 이 폼에 남는다 — 한도 칸을 지금 상태로 바꿔둔다
            $f.find('.cd-opt').html(this.raiseBox());
            this.drawHead();
        }

        // 거절되면 입력을 지우지 않는다 — 기본 동작(load)은 폼을 다시 그린다
        const r = await this.post(`/adm/ajax/${this.owner}/car/apply`, data,
            mbNo ? '장비를 등록하고 연결했습니다.' : '장비를 등록했습니다.', () => {});
        if (!r) return;

        if (mbNo) return this.goTarget('driver', mbNo);
        this.sideTab = 'car';              // 방금 만든 장비가 왼쪽에서도 보이게
        this.load(() => this.goTarget('car', r.num));
    }

    /** 차량번호 중복 안내 — 새 장비 입력에서만 쓴다 */
    checkPlate(v) {
        const id = String(v || '').replace(/ /g, '');
        if (id.length < 3) return;

        $.getJSON('/adm/ajax/member/carnum/check', { mb_no: this.owner, id, driver_no: this._newCarFor || '' }, (d) => {
            d.result ? $j.F.Admin.Util.tsalert('error', d.message || `차량번호 중복 => ${d.mem_name}`)
                     : $j.F.Admin.Util.tsalert('success', '차량번호 중복없습니다.');
        });
    }

    /** 새 장비 입력을 접는다 — 소속자 추가에서 왔으면 그 사람으로, 아니면 연결관리로 */
    onNewCarSkip() {
        this._newCarFor ? this.goTarget('driver', this._newCarFor) : this.goOwner('link');
    }

    /** 장비 폼(`$j.F.CarSelect`)의 값 — 필수가 비면 알리고 null */
    carFormData(mbNo) {
        const s = $j.F.CarSelect;
        const data = {
            car_type: s.getCarType(), depth1: s.getDepth1(), depth2: s.getDepth2(),
            jungo:    s.getJungo(),   number: s.getNumber(), option: s.getOption(),
            mb_no:    mbNo || '',
        };
        if (!data.car_type || !data.depth1 || !data.depth2 || (data.car_type !== '리프트' && !data.jungo)) {
            $j.Ui.Dialog.alert('필수항목을 모두 입력해주세요.');
            return null;
        }
        return data;
    }

    /** 불러와서 그리는 본문 (변경이력·가입비내역) */
    remoteView($v, url, render) {
        $v.html(`<div class="cd-empty">불러오는 중…</div>`);
        const want = this.view;
        $.getJSON(url, (d) => {
            if (this.view !== want) return;   // 그 사이 다른 걸 눌렀으면 버린다
            $v.html(render(d));
        });
    }

    /* ── 업체 홈 ── */

    /**
     * 모달을 열면 처음 보이는 화면 — **업체 단위로 정해지는 것**을 둔다.
     *
     * 처음엔 연결관리(장비↔기사 표)가 첫 화면이었는데, 그건 '무엇을 할지 이미 정하고 들어온
     * 사람'의 화면이다. 열자마자 표부터 보이면 이 업체가 몇 대까지 쓸 수 있는지, 자리가 남았는지는
     * 아무 데도 안 나왔다 — 장비를 못 넣는 이유가 대개 그것인데.
     *
     * 지금은 한도 하나만 있다. 업체 단위 값이 더 생기면 여기 붙인다.
     */
    homeView() {
        const cap = this.data.cap || { max: 0, used: 0 };

        return `<div class="cd-home">
            <section class="cd-card">
                <h4 class="cd-card__head">최대 장비대수</h4>

                <div class="cd-cap" id="cdCap"></div>

                <details class="cd-card__desc cd-card__desc--fold">
                    <summary>한도 규칙 보기</summary>
                    <p><b>이 업체가 동시에 굴릴 수 있는 장비 수</b>입니다. 한도가 찼으면 장비를
                       새로 등록할 수 없고, 등록하려면 먼저 한도를 늘리거나 쓰던 장비를
                       매각·삭제해야 합니다.</p>

                    <dl>
                        <dt>사용중</dt>
                        <dd>지금 살아있는 장비만 셉니다. <b>매각·삭제한 장비는 자리를 비웁니다</b> —
                            기록은 남지만 한도는 도로 늘어납니다.</dd>

                        <dt>늘리는 기준</dt>
                        <dd>첫 1대는 회원가입에 딸린 몫이라 수발주자가 되면 자동으로 열립니다.
                            <b>2대째부터가 추가등록비(가입비)를 받는 자리</b>입니다 —
                            받은 내역은 위 <b>가입비 내역</b>에서 봅니다.</dd>

                        <dt>줄일 때</dt>
                        <dd>지금 쓰는 대수보다 작게는 못 줄입니다. 먼저 장비를 매각·삭제한 뒤
                            줄여주세요. 넣을 수 있는 값은 0~99 입니다.</dd>

                        <dt>기록</dt>
                        <dd>바꾸면 누가 언제 몇 대에서 몇 대로 바꿨는지 <b>변경 이력</b>에 남습니다.</dd>
                    </dl>
                </details>
            </section>

            <section class="cd-card cd-card--sum">
                <h4 class="cd-card__head">현황</h4>
                <div class="cd-home__nums">
                    <div class="cd-num"><span class="cd-num__v">${this.data.drivers.length}</span>
                        <span class="cd-num__k">기사</span></div>
                    <div class="cd-num"><span class="cd-num__v">${cap.used}</span>
                        <span class="cd-num__k">사용중 장비</span></div>
                    <div class="cd-num"><span class="cd-num__v">${this.data.cars.filter((c) => c.ended_at).length}</span>
                        <span class="cd-num__k">매각·삭제</span></div>
                    <div class="cd-num"><span class="cd-num__v">${this.data.cars.filter((c) => !c.ended_at && !c.mb_no).length}</span>
                        <span class="cd-num__k">미배정 장비</span></div>
                </div>
            </section>
        </div>`;
    }

    /* ── 연결관리 ── */

    linkView() {
        const live = this.data.cars.filter((c) => !c.ended_at);
        if (!live.length) return `<div class="cd-empty">사용중인 장비가 없습니다.</div>`;

        const rows = live.map((c) => `<tr>
            <td><b>${this._esc(c.car_platenumber) || '번호없음'}</b>
                <div class="cd-spec">[${this._esc(c.car_type) || '-'}] ${this._esc(c.car_str)}</div></td>
            <td>${this.driverSelect(c)}</td>
            <td class="ta-r">
                <button type="button" class="me-btn cd-sell"   data-num="${c.num}"${CarDrivers.tip('sell')}>매각</button>
                <button type="button" class="me-btn cd-delete" data-num="${c.num}"${CarDrivers.tip('delete')}>삭제</button>
            </td>
        </tr>`).join('');

        return `<table class="cd-tbl">
                  <thead><tr><th>장비</th><th>담당기사</th><th class="ta-r">관리</th></tr></thead>
                  <tbody>${rows}</tbody>
                </table>
                <div class="cd-note">담당기사를 바꾸면 바로 반영됩니다.
                  매각·삭제한 장비는 왼쪽 목록에서 골라 복구합니다.</div>`;
    }

    /**
     * 담당기사 셀렉트 — 고를 수 있는 사람은 '장비가 없는 기사' + '지금 이 장비를 몰고 있는 사람'.
     * 퇴사(9)한 기사도 뺀다.
     */
    driverSelect(c) {
        const cur  = c.mb_no ? String(c.mb_no) : '';
        const opts = this.data.drivers.filter((d) => {
            if (String(d.num) === cur) return true;              // 현재 담당은 항상 남긴다
            if (Number(d.driver_status) === 9) return false;      // 퇴사자 제외
            return !d.car_num;                                    // 장비 없는 기사만
        });

        const body = opts.map((d) =>
            `<option value="${d.num}"${String(d.num) === cur ? ' selected' : ''}>${this._esc(d.mem_name) || d.num}</option>`
        ).join('');

        return `<select class="me-select cd-driver" data-num="${c.num}" data-cur="${cur}">
            <option value="">— 미배정 —</option>${body}
        </select>`;
    }

    /**
     * 배정장비 셀렉트(기사정보) — `driverSelect` 의 거울이다.
     * 고를 수 있는 것은 '기사가 없는 사용중 장비' + '지금 이 기사가 모는 장비'.
     *
     * ⚠ `name` 을 주지 않는다. 기사 저장(`onSaveDriver`)이 폼의 `[name]` 을 전부 실어보낸다.
     */
    carSelect(d) {
        const cur  = d.car_num ? String(d.car_num) : '';
        const opts = this.data.cars.filter((c) =>
            !c.ended_at && (String(c.num) === cur || !Util.int(c.mb_no)));

        const body = opts.map((c) =>
            `<option value="${c.num}"${String(c.num) === cur ? ' selected' : ''}>`
            + `${this._esc(c.car_platenumber) || '번호없음'} [${this._esc(c.car_type) || '-'}] ${this._esc(c.car_str)}</option>`
        ).join('');

        return `<select class="me-select cd-car-of" data-mb="${d.num}" data-cur="${cur}">
            <option value="">— 미배정 —</option>${body}
        </select>`;
    }

    /* ── 기사 편집 ── */

    /**
     * **소속자에 딸린 것만** 고친다.
     *
     * 담는 필드는 내가 고른 게 아니라 **소속자 편집화면이 실제로 렌더하는 것**을 그대로 옮겼다
     * (`_col1` · `_col2` 의 소속자 분기). 서버 스코프도 같은 선이다 —
     * `MemberAdminSaveService::allowed('소속자')` 는 `SCOPE_MEMBERS` 만 허용하고
     * owners·company 는 빈 배열이라, 기사타입·소속·사업자·회비는 애초에 저장되지 않는다.
     *
     *   이름 · 생년월일 · 비밀번호 · 핸드폰 · 서비스상태 · 소유자권한 · GPS
     *   + 수발주자면 매칭반경 · 차고지주소
     *
     * ⚠ **입사일은 읽기전용이다.** 편집화면도 소속자에게는 값만 보여준다
     *   ("소속자 입사일은 여기서 안 다룬다" — `_col1`). 한쪽만 열면 두 화면이 갈린다.
     *
     * ⚠ **비밀번호는 비워두면 안 바뀐다.** 서버가 4자 이상일 때만 반영한다
     *   (`MemberController::save` — `php8_str_len($in['mem_pwd']) > 3`).
     *
     * 부분 전송이 안전한 이유 — `saveV2` 가 **없는 키를 DB 값으로 채운다**(fillMissing).
     * 즉 '보내지 않은 필드 = 변경 없음' 이다.
     */
    driverView() {
        const d = this.data.drivers.find((x) => String(x.num) === String(this.sel.id));
        if (!d) return `<div class="cd-empty">기사를 찾을 수 없습니다.</div>`;

        const isDriver = (d.mem_driver_type || this.driverType) === '수발주자';
        const e = (v) => this._esc(v);

        // 탈퇴한 회원은 잠근다 — 편집창(`MemberEdit._lock`)과 **같은 규칙**이다.
        // 살릴 수 있는 버튼은 복구 하나뿐이고, 나머지는 눌러도 되돌릴 대상이 없다:
        // 푸시·앱로그수집·토큰리셋은 앱을 못 쓰는 회원에게 보내는 것이고, 저장은 탈퇴상태를
        // 그대로 둔 채 값만 바꿔 "고쳐졌는데 여전히 탈퇴"인 회원을 만든다.
        // 매각·삭제된 장비를 잠그는 것(`showCar` 의 `off`)과 같은 모양으로 둔다.
        const gone = Number(d.mem_status) === 99;

        const field = (label, ctrl, stack) =>
            `<div class="me-field${stack ? ' me-field--stack' : ''}">
                <span class="me-field__label">${label}</span>
                <span class="me-field__ctrl">${ctrl}</span>
            </div>`;
        const sel = (name, list, cur) => `<select class="me-select" name="${name}">`
            + list.map(([v, t]) => `<option value="${v}"${String(cur) === String(v) ? ' selected' : ''}>${t}</option>`).join('')
            + `</select>`;
        const block = (title, body) =>
            `<section class="me-block"><div class="me-block__head">${title}</div>
                <div class="me-block__body">${body}</div></section>`;

        // 편집창(열1 기본정보 · 열2 소속자설정/보유장비)의 묶음과 순서를 그대로 따른다 —
        // 같은 값을 늘 같은 자리에서 찾게 해야 익숙함이 유지된다.
        const basic = field('이름',
                `<input type="text" class="me-input" name="mem_name" maxlength="30" value="${e(d.mem_name)}">`)
            + field('생년월일',
                `<input type="text" class="me-input is-date js-date" name="mem_birthday" maxlength="10"`
                + ` value="${e(d.mem_birthday)}" placeholder="YYYY-MM-DD" autocomplete="off">`)
            + field('핸드폰',
                `<input type="tel" class="me-input" id="cdPhone" name="mem_phone" maxlength="15"`
                + ` value="${e(d.mem_phone)}" autocomplete="off">`)
            + field('비밀번호',
                `<input type="password" class="me-input" name="mem_pwd" value="" autocomplete="new-password"`
                + ` placeholder="바꿀 때만 입력">`)
            // 입사일 — 소속자 행(`g5_member_drivers.co_enter_date`). 저장은 `SCOPE_DRIVERS` 가 소속자에게만 쓴다
            + field('입사일',
                `<input type="text" class="me-input is-date js-date" name="co_enter_date" maxlength="10"`
                + ` value="${e(d.co_enter_date)}" placeholder="YYYY-MM-DD" autocomplete="off">`)
            + field('서비스상태', sel('mem_status', [[0, '등록'], [1, '대기'], [98, '탈퇴신청'], [99, '탈퇴']], d.mem_status))
            // ⚠ **실명·추가핸드폰은 두지 않는다.** 둘 다 `g5_member_owners`(소유자당 한 행) 값인데
            //   빼는 이유가 다르다.
            //
            //   실명(`real_name`) — 소속자는 그 행이 거의 없어(1,342명 중 10명) 조인이 소유자 행을
            //   준다. 띄우면 **사장의 실명이 그 사람 것처럼 보인다.** 라벨로 덮을 수 있는 오류가 아니다.
            //
            //   추가핸드폰(`mem_phone2`) — 값 자체는 맞지만 **소속자에게는 쓰이는 곳이 없다.**
            //   읽는 곳이 둘뿐이고 둘 다 소유자 전용이다: 본인인증 번호대조(`InicisClient` —
            //   `g5_member_owners` 를 mb_no 로 보므로 소속자는 애초에 빈값)와 CMS 자동이체 신청
            //   (회비라 소유자 일이다). 소속자 편집칸에서는 읽을 이유가 없다.
            //
            //   마케팅동의(`is_ad`) — 역시 소유자 행 값이고, 편집창도 `@if($isOwner)` 로 소유자에게만
            //   보여준다. 소속자 화면에 두면 이 사람이 동의한 것처럼 읽히는데 그 사람 값이 아니다.
            //
            //   셋 다 편집창(`_col1`)에는 남는다 — 거기는 소유자를 여는 화면이다.
            ;

        // ⚠ 이름은 컬럼명(is_owner_auth)이 아니라 **`is_owner_auth_sel`** 이다 —
        //   서버가 그 이름을 읽어 컬럼에 넣는다(MemberController::save). 컬럼명으로 보내면
        //   `fillMissing` 이 옛 값으로 되채워 **조용히 저장되지 않는다.**
        // ⚠ id 는 `cd_` 를 붙인다. 편집창에도 같은 스위치가 있어(_switch.blade) id 가 겹치면
        //   `<label for>` 가 앞엣것을 가리켜 **모달을 눌렀는데 뒤 화면 값이 바뀐다.**
        const sw = (name, on) =>
            `<span class="me-switch">
                <input type="hidden" name="${name}" value="0">
                <span class="custom-control custom-switch">
                    <input type="checkbox" class="custom-control-input" id="cd_sw_${name}"
                           name="${name}" value="1" ${on ? 'checked' : ''}>
                    <label class="custom-control-label" for="cd_sw_${name}"></label>
                </span>
                <span class="me-switch__lbl">ON</span>
             </span>`;

        // 위치동의 — **이 소속자 본인의 법적 동의**라 여기서 바꿀 수 있다 (마케팅과 다르다).
        // 저장처는 회원 컬럼이 아니라 동의이력(g5_terms_consent_log)이라 화이트리스트를 못 탄다.
        // 모달도 `/adm/ajax/member/v2/save` 로 보내므로 `saveV2` 가 뒤이어 집어 기록한다.
        // 상태는 목록에 안 담고 고를 때 따로 가져온다 — 사람당 쿼리 3개짜리라 목록에 붙이면 비싸다.
        const lc = this._lc[d.num];
        const locSel = lc
            ? `<select class="me-select" name="loc_consent" data-prev="${e(lc.state)}">`
              + (lc.state === 'stale' ? `<option value="stale" selected>재동의필요</option>` : '')
              + [['none', '무응답'], ['refuse', '미동의'], ['agree', '동의']]
                  .map(([v, t]) => `<option value="${v}"${lc.state === v ? ' selected' : ''}>${t}</option>`).join('')
              + `</select>`
            : `<span class="me-ro">확인중…</span>`;

        const sosok = field('소유자권한', sw('is_owner_auth_sel', Number(d.is_owner_auth) === 1))
            + (isDriver ? field('위치동의', locSel) : '')

            + field('GPS', sel('is_gps', [[0, '켜기'], [1, '끄기(관리폰)'], [2, '소유자위치 따라가기']], d.is_gps));

        // 배정은 여기서 바로 바꾼다. 탈퇴·퇴사자는 붙일 수 없어 값만 보인다(서버도 거절한다).
        const car = field('배정장비', (gone || Number(d.driver_status) === 9)
                ? `<span class="me-ro">${e(d.car_platenumber) || '미배정'}</span>`
                : this.carSelect(d) + `<span class="me-field__hint">바꾸면 바로 반영</span>`)
            + (isDriver
                ? field('매칭반경',
                    `<input type="text" class="me-input js-num" name="mem_areakm" value="${e(d.mem_areakm)}"`
                    + ` autocomplete="off" placeholder="숫자만" style="width:70px;flex:0 0 70px">`
                    + `<span class="me-field__hint">Km</span>`)
                  + field('차고지주소',
                    `<input type="text" class="me-input" id="cdParkAddr" name="car_parkaddress" readonly`
                    + ` value="${e(d.car_parkaddress)}" placeholder="클릭해서 주소 검색">`
                    + `<input type="hidden" name="gps" value="${e(d.gps)}">`
                    + `<input type="hidden" name="is_gps_update" value="">`, true)
                : '');

        // 두 칸으로 나눈다 — 한 줄로 쌓으면 넓은 모달에서 오른쪽이 통째로 비고 아래로만 길어진다.
        // 왼쪽 6칸(기본정보) · 오른쪽 5칸(소속자 설정 + 장비·매칭) 으로 길이를 맞췄다.
        return `<form class="cd-form" id="cdDriverForm" data-num="${d.num}" onsubmit="return false">
            ${gone ? `<div class="cd-note">탈퇴한 회원입니다. 복구해야 고칠 수 있습니다.</div>` : ''}
            <div class="cd-form__cols">
                <div class="me-col">${block('기본정보', basic)}${this.statusCard(d)}</div>
                <div class="me-col">${block('소속자 설정', sosok)}${block('장비 · 매칭', car)}</div>
            </div>
            <div class="cd-form__act">
                ${gone
                    ? `<button type="button" class="me-btn cd-recover" data-num="${d.num}">복구</button>`
                    : `<button type="button" class="me-save" id="cdDriverSave">저장</button>
                       ${this.driverActions(d)}`}
            </div>
        </form>`;
    }

    /**
     * 고른 기사의 상태카드 — 고칠 수 없는 값(현황)만 모은다.
     *
     * 편집칸과 섞지 않는 이유: 폼 안에 읽기전용 값이 끼면 저장이 무엇을 바꾸는지 흐려진다.
     * 여기 있는 것은 전부 다른 곳에서 만들어지는 값이라 이 화면에서 고칠 수 없다 —
     * 앱버전은 기기가, 로그인은 접속이, 수주·서류는 다른 업무가 쓴다.
     */
    statusCard(d) {
        const e = (v) => this._esc(v);

        // 기기 — 기종(판매명+코드, 서버 device_label) · OS. 둘 다 없으면 앱으로 접속한 적이 없다는 뜻이다.
        const dev  = [d.device_label || [d.device_co, d.device_name].filter(Boolean).join(' '), d.device_os].filter(Boolean).join(' · ');
        // 푸시토큰 — 없으면 이 회원에게 알림이 안 간다. 앱 문의의 첫 확인점.
        // (값 자체는 받지 않는다. 서버가 길이만 보내온다 — 토큰은 화면에 뿌릴 값이 아니다)
        const tok  = Number(d.token_len) > 10;
        const soo  = Number(d._soo) || 0;
        const prev = Number(this.data.prevSoo) || 0;
        const lack = Number(d._doc_cnt) !== Number(d._doc_max);
        const ver  = this.verHtml(d);

        const row = (dt, dd) => `<dt>${dt}</dt><dd>${dd}</dd>`;
        const none = (t) => `<span class="me-info__none">${t}</span>`;

        // 다른 묶음(기본정보·소속자 설정)과 **같은 카드**로 그린다 — 폼 위에 혼자 다른 모양으로
        // 떠 있으면 이 화면의 일부가 아니라 알림처럼 읽힌다.
        return `<section class="me-block cd-info">
            <div class="me-block__head">정보</div>
            <div class="me-block__body me-info">
            <dl>
                ${row('최근접속', d._seen ? e(d._loginDate) : none('미접속'))}
                ${row('수주',
                    `이달 <b>${soo}</b>건 <span class="me-info__sep">·</span> 전달 <b>${prev}</b>건`
                    + ` <span class="me-info__axis" title="전달 수주는 월배치가 업체(소유자) 단위로 집계합니다. 이 소속자 개인의 값이 아닙니다.">업체</span>`)}
                ${row('앱', ver
                    ? `${ver}${dev ? `<br><span class="me-info__dev">${e(dev)}</span>` : ''}`
                    : none('앱 접속기록 없음'))}
                ${row('푸시토큰', tok ? '있음' : `<span class="me-badge me-badge--danger">없음</span>`)}
                ${row('서류', `<b>${d._doc_cnt}</b>/${d._doc_max}`
                    + (lack ? ` <span class="me-badge me-badge--warn">미비</span>` : ''))}
            </dl>
            </div>
        </section>`;
    }

    /**
     * 고른 기사에게 하는 것들.
     *
     * 전부 **편집창 하단 액션바와 같은 동작을 같은 코드로** 부른다 (`$j.p.act`) —
     * 여기서 다시 구현하면 확인문구·엔드포인트가 조용히 갈린다. 그 메서드들이 원래
     * '지금 화면의 회원'만 보고 있어서 대상 회원번호를 인자로 받게 넓혔다.
     *
     * 여기 남긴 것은 **앱이 안 될 때 쓰는 것**뿐이다 — 푸시가 안 온다는 문의가 이 모달에서
     * 제일 자주 나오고, 토큰·접속기록이 바로 위 '정보' 카드에 있어 보고 바로 누른다.
     * 서류보기·변경로그는 뺐다(요청). '편집창 열기'는 머리줄에 이미 있다 — 겹쳐 두지 않는다.
     *
     * **묶지 않는다.** 편집창 액션바는 버튼이 열 몇 개라 계열로 접었지만, 여기는 넷이라
     * 한 줄에 다 선다. 묶음 테두리가 오히려 저장 버튼과 경쟁하는 덩어리를 만든다.
     * 회원을 지우거나 되돌리는 것(탈퇴·복구)은 두지 않았다. 이 모달은 배정을 다루는 자리고,
     * 그 둘은 되돌리기 어려워 편집창에서 회원을 똑바로 보고 눌러야 한다.
     */
    driverActions(d) {
        return `<div class="cd-acts" data-num="${d.num}">
            <button type="button" class="me-btn cd-act" data-act="push">푸시테스트</button>
            <button type="button" class="me-btn cd-act" data-act="diag">앱로그수집</button>
            <button type="button" class="me-btn cd-act" data-act="llog">로그인기록</button>
        </div>`;
    }

    /**
     * 기사편집 폼을 그리고 날짜칸에 달력을 붙인다.
     *
     * 본창의 `_initDatePickers` 는 페이지 초기화 때 `.js-date` 를 훑으므로 **그 뒤에 만들어지는
     * 이 폼은 못 잡는다.** 설정을 여기서 새로 쓰지 않고 그 메서드에 범위를 넘겨 같은 것을 쓴다.
     */
    showDriver($v) {
        $v.html(this.driverView());
        this._afterDriverForm($v);
        this.fillLocConsent($v);
    }

    /**
     * 기사 폼을 그린 뒤 거는 것들. 위치동의가 늦게 와서 폼을 다시 그릴 때도 같은 것을 걸어야
     * 해서 따로 뒀다 — 한쪽만 걸면 그때부터 달력·하이픈이 죽는다.
     *
     * 입력단 검사는 본창과 **같은 것**을 쓴다. 셋 다 페이지 초기화 때 한 번 거는 것이라
     * 나중에 만들어지는 이 폼은 그 스캔에 안 잡힌다 → 여기서 같은 함수를 다시 건다.
     */
    _afterDriverForm($v) {
        // 탈퇴회원은 값만 보여준다. 달력·하이픈·중복검사는 고칠 수 있을 때 필요한 것들이라
        // 여기서 끝낸다 — 잠근 칸에 걸어봐야 돌지 않는다.
        const d = this.data.drivers.find((x) => String(x.num) === String(this.sel.id));
        if (d && Number(d.mem_status) === 99) {
            $v.find('#cdDriverForm').find('input,select,textarea').prop('disabled', true);
            return;
        }

        if ($j.p && $j.p._initDatePickers) $j.p._initDatePickers($v.find('.js-date'));
        if ($v.find('#cdPhone').length) $j.Common.Form.registerPhoneHandler('cdPhone');   // 하이픈 자동
        $v.find('.js-num').on('keyup', (e) => {                                            // 숫자만
            e.target.value = e.target.value.replace(/[^0-9.]/g, '');
        });
        $v.find('#cdPhone').on('blur', () => this.checkPhone());
    }

    /**
     * 핸드폰 중복검사 — 본창 `Actions.checkPhone` 과 **같은 엔드포인트·같은 규칙**이다.
     * 그 메서드를 그대로 못 부르는 이유는 `#me_mem_phone` 과 편집중인 회원번호를 박아 읽기
     * 때문이다. 여기서 보는 대상은 왼쪽에서 고른 기사다.
     */
    checkPhone() {
        const $f = this.$root.find('#cdDriverForm');
        const p  = String($f.find('#cdPhone').val() || '');
        if (p.length < 3) return;

        $.getJSON('/adm/ajax/member/check', { mb_no: $f.data('num'), O: this.owner, P: p }, (d) => {
            String(d.dup || '').includes('P')
                ? $j.F.Admin.Util.tsalert('error', '이미 사용중인 핸드폰번호 입니다.')
                : $j.F.Admin.Util.tsalert('success', '사용가능한 핸드폰번호 입니다.');
        });
    }

    /**
     * 차고지 주소 선택 — **좌표를 함께 받아야 한다.**
     *
     * `car_parkaddress` 는 글자일 뿐이고, 당일외 매칭이 실제로 보는 것은
     * `g5_member_locations` 의 좌표다 (`MemberGpsService::update`, `is_gps_update=1` 일 때만 갱신).
     * 주소만 바꾸고 좌표를 안 보내면 **글자와 거리계산이 갈린다.**
     *
     * 지도 모듈은 결과를 전역 `callbackAddress` 로 돌려준다 — 본창(MemberEdit)이 이미 그 이름을
     * 쓰고 있으므로, 여는 동안만 우리 것으로 바꿔 끼우고 되돌린다.
     */
    openParkAddress() {
        const $f    = this.$root.find('#cdDriverForm');
        const prev  = window.callbackAddress;

        window.callbackAddress = (arr) => {
            $f.find('#cdParkAddr').val(arr.addr);
            $f.find('[name=gps]').val(`${arr.lat},${arr.long}`);
            $f.find('[name=is_gps_update]').val(1);
            window.callbackAddress = prev;
        };
        $j.F.KakaoMap.open($f.find('[name=gps]').val(), '0', '', { panel: true, closeBtn: true, src: '/adm/map' });
    }

    /* ── 장비 편집 ── */

    /** 기종 선택은 공용 `$j.F.CarSelect` 가 그린다 — 여기서 다시 짜면 두 벌이 된다 */
    carView($v) {
        const c = this.data.cars.find((x) => String(x.num) === String(this.sel.id));
        if (!c) { $v.html(`<div class="cd-empty">장비를 찾을 수 없습니다.</div>`); return; }

        const off = !!c.ended_at;
        const e = (x) => this._esc(x);
        const field = (label, ctrl, stack) =>
            `<div class="me-field${stack ? ' me-field--stack' : ''}">
                <span class="me-field__label">${label}</span>
                <span class="me-field__ctrl">${ctrl}</span>
            </div>`;

        const body = field('기종',
                `<select id="cd-cartype" class="me-select"></select>`
                + `<select id="cd-depth1"  class="me-select"></select>`
                + `<select id="cd-depth2"  class="me-select"></select>`
                + `<select id="cd-jungo"   class="me-select"></select>`, true)
            + field('차량번호',
                `<input id="cd-number" class="me-input" type="text" autocomplete="off" placeholder="차량번호 입력">`)
            + field('옵션', `<span id="cd-option"></span>`, true)
            + field('담당기사', off
                ? `<span class="me-ro">${Util.int(c.mb_no) ? $j.F.Admin.Member.Pop.cell(c.mb_no, c.driver_name) : (e(c.driver_name) || '미배정')}</span>`
                : this.driverSelect(c) + `<span class="me-field__hint">바꾸면 바로 반영</span>`);

        $v.html(`<div class="cd-form" id="cdCarForm" data-num="${c.num}">
            ${off ? `<div class="cd-note">매각·삭제된 장비입니다. 복구해야 고칠 수 있습니다.</div>` : ''}
            <section class="me-block">
                <div class="me-block__head">장비정보</div>
                <div class="me-block__body">${body}</div>
            </section>
            <div class="cd-form__act">
                ${off ? `<button type="button" class="me-btn cd-restore" data-num="${c.num}"${CarDrivers.tip('restore')}>복구</button>`
                      : `<button type="button" class="me-save" id="cdCarSave">저장</button>
                         <button type="button" class="me-btn cd-sell"   data-num="${c.num}"${CarDrivers.tip('sell')}>매각</button>
                         <button type="button" class="me-btn cd-delete" data-num="${c.num}"${CarDrivers.tip('delete')}>삭제</button>`}
            </div>
        </div>`);

        $j.F.CarSelect.initialize({
            idList: { car_type: 'cd-cartype', depth1: 'cd-depth1', depth2: 'cd-depth2',
                      jungo: 'cd-jungo', number: 'cd-number', option: 'cd-option' },
            v: c,
        });
    }

    /**
     * 위치동의 상태를 채운다 — 고른 사람 것만, 한 번만.
     *
     * 목록에 함께 담지 않는 이유는 **한 사람당 쿼리가 3개**여서다 (약관 2건의 현행동의 확인 +
     * 최근이력 1건). 소속자 22명인 업체면 목록 한 번 여는 데 66개가 붙는다.
     * 한 번 받은 값은 `_lc` 가 들고 있어 사람을 오갈 때 다시 묻지 않는다.
     */
    fillLocConsent($v) {
        const d = this.data.drivers.find((x) => String(x.num) === String(this.sel.id));
        if (!d) return;
        if ((d.mem_driver_type || this.driverType) !== '수발주자') return;
        if (this._lc[d.num]) return;                 // 이미 그렸다

        const want = d.num;
        $.getJSON(`/adm/ajax/member/${d.num}/loc-consent`, (r) => {
            if (!r || !r.result) return;
            this._lc[want] = r;
            // 그 사이 다른 사람을 눌렀으면 다시 그리지 않는다
            if (String(this.sel.id) !== String(want) || this.sel.kind !== 'driver') return;
            $v.html(this.driverView());
            this._afterDriverForm($v);
        });
    }

    /* ── 서류 ── */

    /**
     * 서류 — 서류목록 화면(`/adm/member/doc`)과 **같은 것을 본다** (`DocService::rowsForOwner`).
     * 자르는 축만 다르다.
     *
     *   업체 축   업체 전체. 사람별로 묶어 보인다 — **소유자 본인이 맨 위**고, 사업자등록증·
     *             통장사본 같은 업체서류가 거기 붙는다(실데이터 100%가 소유자 행이다).
     *   기사      그 사람의 **기사서류** (면허증·이수증·산재보험증)
     *   장비      그 장비의 **장비서류** (등록증·보험증·비파괴·제원표 …)
     *
     * ⚠ 축을 가르는 것은 **종류**(`DocService::typeOf`)지 `mb_carnum` 이 아니다. 그 값은
     *   '올릴 당시 몰던 장비'라 사람 서류의 99%에도 장비가 박혀 있다 — 그것만 보면 장비를
     *   골랐을 때 남의 면허증이 딸려 나온다.
     *
     * 미분류(커스텀 100+)는 두 축 어디에도 안 나오고 **업체 전체에서만** 보인다.
     *
     * 고치는 것은 **서류 이동**(잘못된 칸에 올린 사진을 다른 종류로)과 이미지 편집뿐이다.
     * 삭제·유효기간 수정은 서류목록 화면이 하는 일이다.
     */
    docsView($v) {
        const isOwnerAxis = !this.sel.kind || this.isOwnerSel();
        const isDrv       = this.sel.kind === 'driver';
        // 업체 축도 **한 사람(소유자)** 을 본다 — 업체서류가 소유자 행에 붙어 있어서
        // 그 사람을 부르면 업체서류 + 소유자 본인 기사서류가 함께 온다.
        const q    = isOwnerAxis ? { mb_no: this.owner }
                                 : (isDrv ? { mb_no: this.sel.id } : { car_num: this.sel.id });
        const who  = isOwnerAxis ? `${this.driverName(this.owner) || '소유자'} (업체·소유자 서류)`
                                 : (isDrv ? this.driverName(this.sel.id) : this.carName(this.sel.id));
        const want = this.view;

        $v.html(`<div class="cd-empty">불러오는 중…</div>`);

        $.getJSON(`/adm/ajax/${this.owner}/docs`, q, (d) => {
            if (this.view !== want) return;                 // 그 사이 다른 걸 눌렀으면 버린다
            this._docs = { list: (d && d.list) || [], targets: (d && d.targets) || [], who, isOwnerAxis };
            this.drawDocs($v);
        }).fail((xhr) => {
            if (this.view !== want) return;
            $v.html(`<div class="cd-empty">서류를 불러오지 못했습니다. (${xhr.status})</div>`);
        });
    }

    /**
     * 받아 둔 서류(`this._docs`)를 그린다. 이동 모드(`this._docMove`)면 **장마다 한 칸**으로 편다 —
     * 평소 카드는 여러 장을 하나로 묶어 보여서, 면허증 칸에 섞인 등록증 한 장만 고를 수가 없다.
     */
    drawDocs($v) {
        const { list, who, isOwnerAxis } = this._docs;
        const moving = !!this._docMove;
        const head = `<div class="cd-sum">${this._esc(who)} — 서류 <b>${list.length}</b>건
            ${list.length ? `<button type="button" class="me-btn cd-docmove-toggle" style="margin-left:auto">
                <i class="fa-solid fa-right-left"></i> ${moving ? '이동 그만' : '서류 이동'}</button>` : ''}</div>`;

        if (!list.length) {
            $v.html(head + `<div class="cd-empty">등록된 서류가 없습니다.</div>`);
            return;
        }
        if (moving) { $v.html(head + this.docMoveView()); this.syncDocMove(); return; }

        // 뷰어에 넘길 목록 — **이미지가 있는 것만**. 없는 칸이 섞이면 넘기다 빈 화면이 나온다.
        // 카드의 `data-i` 는 이 배열의 자리라 카드 순번과 다르다. 묶어 그릴 때도 하나로 이어져
        // 그룹을 넘나들며 넘길 수 있다.
        this._docItems = [];

        // 업체 축은 **업체서류와 소유자 개인서류를 갈라 놓는다.** 한 사람 행에 함께
        // 저장돼 있을 뿐 성격이 다르다 — 사업자등록증·통장사본은 업체가 내는 것이고
        // 면허증·이수증은 그 사람이 내는 것이다. 섞어 놓으면 무엇이 업체 몫인지 안 보인다.
        const body = isOwnerAxis ? this.docSections(list) : this.docGrid(list);

        $v.html(head + body
            + `<div class="cd-note">이미지를 누르면 크게 보고 편집할 수 있습니다.
                 삭제·유효기간 수정은 서류창에서 합니다.</div>`);
    }

    /**
     * 서류 이동 — 장을 골라 다른 서류 종류로 옮긴다. 서버 규칙은 서류창과 같은 `DocMoveService`.
     * 장마다 자기 회원의 칸으로 간다 (소유자 번호만 넘기면 된다).
     */
    docMoveView() {
        const pages = [];
        this._docs.list.forEach((r) => {
            const imgs = r._imgs || [];
            if (imgs.length) imgs.forEach((x, i) => pages.push({
                num: x.docNum, url: x.url, title: r._title + (imgs.length > 1 ? ` (${i + 1}/${imgs.length})` : ''),
                who: r.mem_name }));
            else pages.push({ num: r.num, url: '', title: r._title, who: r.mem_name });
        });

        const cards = pages.map((p) => `
            <button type="button" class="cd-doc cd-doc--pick" data-pick="${Number(p.num)}">
                <span class="cd-doc__img"><span class="cd-doc__chk"><i class="fa-solid fa-check"></i></span>${p.url
                    ? `<img src="${this._esc(p.url)}" alt="" loading="lazy">`
                    : `<span class="cd-doc__none">이미지 없음</span>`}</span>
                <span class="cd-doc__tit">${this._esc(p.title)}</span>
                ${p.who ? `<span class="cd-doc__exp">${this._esc(p.who)}</span>` : ''}
            </button>`).join('');

        const opts = this._docs.targets.map((t) =>
            `<option value="${Number(t.num)}" data-etc="${t.is_etc ? 1 : 0}">${this._esc(t.name)}</option>`).join('');

        return `<div class="cd-note cd-note--inline" style="margin:0 0 10px">옮길 사진을 고르세요.
                사진은 그대로이고 <b>어느 서류 칸에 속하는지</b>만 바뀝니다 — 각자 자기 회원의 칸으로 갑니다.</div>
            <div class="cd-docs">${cards}</div>
            <div class="cd-docmove">
                <span class="cd-docmove__cnt"><b class="cd-docmove-cnt">0</b>장 선택</span>
                <select class="me-select cd-docmove-type"><option value="">옮길 서류 종류</option>${opts}</select>
                <input type="text" class="me-input cd-docmove-title" maxlength="45" placeholder="서류명" style="display:none">
                <button type="button" class="me-btn me-btn--accent cd-docmove-go" disabled>옮기기</button>
            </div>`;
    }

    syncDocMove() {
        const $v = this.$root.find('#cdView');
        const n = $v.find('.cd-doc--pick.is-picked').length;
        const $t = $v.find('.cd-docmove-type');
        const etc = Number($t.find(':selected').data('etc')) === 1;
        const $title = $v.find('.cd-docmove-title').toggle(etc);
        $v.find('.cd-docmove-cnt').text(n);
        $v.find('.cd-docmove-go').prop('disabled', !n || !$t.val() || (etc && !$title.val().trim()));
    }

    async onDocMove() {
        const $v = this.$root.find('#cdView');
        const nums = $v.find('.cd-doc--pick.is-picked').map((i, el) => $(el).data('pick')).get();
        const $t = $v.find('.cd-docmove-type');
        const etc = Number($t.find(':selected').data('etc')) === 1;
        const title = $v.find('.cd-docmove-title').val().trim();
        const to = etc ? `기타 서류 「${title}」` : $t.find(':selected').text();

        const ok = await $j.Ui.Dialog.confirm(`사진 ${nums.length}장을 ${to}(으)로 옮길까요?`, {
            desc: '사진은 그대로이고 서류 칸만 바뀝니다. 회원 앱의 서류 화면에도 바로 반영됩니다.',
            confirm: '옮기기',
        });
        if (!ok) return;

        $v.find('.cd-docmove-go').prop('disabled', true);
        this._docMove = false;   // 옮긴 뒤엔 평소 보기로 — post() 가 목록과 본문을 다시 그린다
        this.post('/adm/ajax/doc/move', { mb_no: this.owner, nums, type_num: $t.val(), title },
            `${nums.length}장을 옮겼습니다.`);
    }

    /**
     * 업체 축 — 성격이 다른 둘을 갈라 놓는다.
     *
     *   업체서류    사업자등록증·통장사본. 업체가 하나씩 낸다 (`DocService::TYPE_COMPANY`)
     *   소유자 서류  면허증·이수증·산재보험증 등 이 사람 몫 + 분류 못 한 커스텀
     *
     * 둘 다 소유자 행에 저장돼 있어 조회로는 안 갈린다 — 종류로 가른다.
     * 한쪽이 비면 그 칸은 그리지 않는다 (빈 제목만 남으면 뭘 봐야 할지 헷갈린다).
     */
    docSections(list) {
        const T = CarDrivers.DOC_TYPE_COMPANY;
        const company = list.filter((r) => Number(r._type) === T);
        const mine    = list.filter((r) => Number(r._type) !== T);

        const sec = (title, hint, rows) => rows.length ? `<section class="cd-docsec">
            <h5 class="cd-docsec__head">${title}
                <span class="cd-docsec__hint">${hint}</span></h5>
            ${this.docGrid(rows)}
        </section>` : '';

        // 업체서류를 먼저 — 업체 축에서 찾는 것은 대개 이쪽이다
        return sec('업체서류', '업체가 하나씩 냅니다', company)
             + sec('소유자 서류', '이 사람 몫입니다', mine)
             || `<div class="cd-empty">등록된 서류가 없습니다.</div>`;
    }

    /** 서류 카드 격자. 뷰어 목록(`_docItems`)에 이어 붙이며 인덱스를 매긴다. */
    docGrid(rows) {
        const TONE = { over: ['danger', '만료됨'], soon: ['warn', '만료임박'] };

        const cards = rows.map((r) => {
            const img  = r.edit_thumb_url || r.thumb_url;
            const full = r.edit_image_url || r.image_url;
            const t    = TONE[r.expire_state];

            // 여러 장짜리 서류는 대표 하나만 그린다. 뷰어에는 그 서류의 **모든 장**을 넣어
            // 카드를 누르면 앞뒤로 넘겨볼 수 있게 한다.
            // 뷰어에 넘길 때 **서류명과 부가정보를 함께 싣는다** — 여러 장을 넘기다 보면
            // 지금 보는 게 어느 서류의 몇 번째 장인지 사진만으로는 알 수 없다.
            const meta = [
                r._typeName,
                Number(r._cnt) > 1 ? `${r._cnt}장` : '',
                r.expire_date && String(r.expire_date).indexOf('0000-') !== 0
                    ? `유효기간 ${r.expire_date}${TONE[r.expire_state] ? ` · ${TONE[r.expire_state][1]}` : ''}` : '',
                r.mem_name ? `기사 ${r.mem_name}` : '',
            ].filter(Boolean).join('  ·  ');

            const cap = (x, i, n) => ({
                url:     x.url,
                caption: r._title + (n > 1 ? ` (${i + 1}/${n})` : ''),
                meta,
                // 편집 버튼이 쓰는 값 — 장마다 다르다(같은 서류라도 장별로 다른 이미지다)
                docNum:  x.docNum,
                imgId:   x.imgId,
            });
            const imgs = (r._imgs && r._imgs.length)
                ? r._imgs.map((x, i) => cap(x, i, r._imgs.length))
                : (full ? [cap({ url: full, docNum: r.num, imgId: r.img_rel_id || r.orgimg_rel_id }, 0, 1)] : []);
            let idx = -1;
            if (imgs.length) {
                idx = this._docItems.length;
                this._docItems.push(...imgs);
            }

            return `<button type="button" class="cd-doc${imgs.length ? '' : ' is-empty'}"
                            ${imgs.length ? `data-i="${idx}"` : 'disabled'}>
                <span class="cd-doc__img">${img
                    ? `<img src="${this._esc(img)}" alt="${this._esc(r._title)}" loading="lazy">`
                    : `<span class="cd-doc__none">이미지 없음</span>`}
                    ${Number(r._cnt) > 1
                        ? `<span class="cd-doc__cnt" title="${r._cnt}장">${r._cnt}</span>` : ''}</span>
                <span class="cd-doc__tit">${this._esc(r._title)}
                    <span class="cd-doc__type is-t${r._type}">${this._esc(r._typeName)}</span></span>
                ${r.expire_date && String(r.expire_date).indexOf('0000-') !== 0
                    ? `<span class="cd-doc__exp">${this._esc(r.expire_date)}
                         ${t ? `<span class="cd-badge cd-badge--${t[0]}">${t[1]}</span>` : ''}</span>`
                    : ''}
            </button>`;
        }).join('');

        return `<div class="cd-docs">${cards}</div>`;
    }

    /* ── 상담내역 ── */

    /**
     * 상담내역 — **왼쪽에서 고른 기사**(없으면 소유자)의 것.
     *
     * 편집창 우측(열4)과 **같은 인라인 영역**을 그린다 (`Counsel.mountInline`) — 마크업도
     * 렌더러도 하나다. 길게 눌러 그 자리에서 날짜·유형·내용을 고치는 것까지 그대로 온다.
     *
     * 대상·뿌리를 되돌리는 것은 **부른 쪽 책임**이다 (`go`·모달 닫힘에서 `releaseCounsel`).
     */
    counselView($v) {
        const target = this.sel.kind === 'driver' ? this.sel.id : this.owner;
        const who    = this.driverName(target) || `#${target}`;

        $v.html(`<div class="cd-sum">상담내역 — <b>${this._esc(who)}</b>`
              + ` <button type="button" class="me-btn me-btn--accent me-btn--icon" id="cdCounselAdd"`
              + ` data-tip-hover data-tip="상담내역 추가" aria-label="상담내역 추가">`
              + `<i class="fa-solid fa-plus"></i></button></div>`
              + `<div id="cdCounsel" class="cd-counsel"></div>`);

        // 편집창 우측과 **같은 인라인 영역**을 그린다 (빠른입력 + 노트북 목록).
        // 길게 누르면 그 자리에서 날짜·유형·내용을 고치는 것까지 그대로 온다.
        $j.F.Admin.Counsel.mountInline($v.find('#cdCounsel'), target, who);
    }

    /**
     * 상담 축을 편집중인 회원으로 되돌린다.
     *
     * 안 되돌리면 이 화면을 떠난 뒤에도 편집창의 상담 열(빠른입력·목록)이 계속 그 기사 번호를
     * 보고, **사장님 화면에서 쓴 메모가 소속기사에게 저장된다.**
     */
    releaseCounsel() {
        if ($j.F.Admin.Counsel.clearTarget) $j.F.Admin.Counsel.clearTarget();
    }

    /** 빠른입력 토글 — 열4 의 [+] 와 같은 동작 */
    onCounselAdd() { $j.F.Admin.Counsel.openAddCounselModal(); }

    /* ── 변경 이력 · 가입비 내역 ── */

    histHtml(d) {
        const list = d.list || [];
        if (!list.length) return `<div class="cd-empty">이력이 없습니다.</div>`;

        const rows = list.map((r) => {
            // 대상 — 장비·기사 둘 다 없는 사건(한도변경)도 있다
            // 기사는 관리자 회원 표기(`이름#번호` + 회원 팝오버)로 그린다. 걸러내기는 화면에 보이는 글자로 하므로
            // 같은 내용을 글자로도 따로 만든다 — 링크 HTML 을 그대로 hay 에 넣으면 속성 글자까지 걸린다.
            const plate = r.car_platenumber || (r.car_num ? `#${r.car_num}` : '');
            const who = [
                r.car_platenumber ? `<b>${this._esc(r.car_platenumber)}</b>` : plate,
                Util.int(r.mb_no) ? $j.F.Admin.Member.Pop.cell(r.mb_no, r.driver_name) : this._esc(r.driver_name),
            ].filter(Boolean).join(' · ');
            const whoText = [plate, Util.int(r.mb_no) ? `${r.driver_name || '회원'}#${r.mb_no}` : (r.driver_name || '')]
                .filter(Boolean).join(' · ');

            const act = d.actions[r.rel_action] || r.rel_action;
            // 검색은 화면에 보이는 글자로 한다 — 사람이 읽은 것을 그대로 치면 걸려야 한다.
            // 그래서 코드(CONNECT)가 아니라 번역된 이름(연결)까지 담는다.
            const hay = [dayjs(r.created_at).format('YY-MM-DD HH:mm'), act, whoText,
                         r.rel_desc, r.manager_name].filter(Boolean).join(' ').toLowerCase();

            return `<tr data-hay="${this._esc(hay)}">
                <td>${dayjs(r.created_at).format('YY-MM-DD HH:mm')}</td>
                <td><b>${act}</b></td>
                <td>${who || '<span class="cd-none">-</span>'}</td>
                <td>${this._esc(r.rel_desc)}</td>
                <td>${this._esc(r.manager_name) || '<span class="cd-none">시스템</span>'}</td>
            </tr>`;
        }).join('');

        // 서버가 한 번에 다 주므로 걸러내기는 화면에서 한다 — 글자를 칠 때마다 서버에 묻지 않는다.
        return `<div class="cd-find">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <input type="text" class="me-input" id="cdHistFind" autocomplete="off"
                           placeholder="장비번호 · 기사명 · 내용 · 처리자로 걸러내기">
                    <span class="cd-find__cnt" id="cdHistCnt">${list.length}건</span>
                </div>
                <table class="cd-tbl" id="cdHistTbl">
                  <thead><tr><th>일시</th><th>내용</th><th>대상</th><th>비고</th><th>처리자</th></tr></thead>
                  <tbody>${rows}</tbody>
                </table>
                <div class="cd-empty cd-find__none" style="display:none">걸러낸 결과가 없습니다.</div>
                <div class="cd-note">처리자가 '시스템'인 것은 관리자 화면 밖에서 일어났거나
                  옛 배정이력에서 이관된 기록입니다.</div>`;
    }

    /** 변경이력 걸러내기 — 공백으로 나눈 모든 낱말을 다 가진 줄만 남긴다(AND) */
    onHistFind($in) {
        const words = String($in.val() || '').toLowerCase().split(/\s+/).filter(Boolean);
        const $rows = this.$root.find('#cdHistTbl tbody tr');
        let hit = 0;

        $rows.each((_, el) => {
            const hay = el.getAttribute('data-hay') || '';
            const ok  = words.every((w) => hay.includes(w));
            el.style.display = ok ? '' : 'none';
            if (ok) hit++;
        });

        this.$root.find('#cdHistCnt').text(`${hit}건`);
        this.$root.find('.cd-find__none').toggle(hit === 0);
        this.$root.find('#cdHistTbl').toggle(hit > 0);
    }

    logHtml(d) {
        if (!d.cnt) return `<div class="cd-empty">추가등록비 결제내역이 없습니다.</div>`;

        // 납부와 환불을 섞어 보여주되 부호를 분명히 한다 — 절대값만 보면 환불이 납부로 읽힌다
        const rows = d.list.map((r) => {
            const ref = Number(r.is_refund) === 1;
            return `<tr>
                <td>${dayjs(r.datetime).format('YYYY-MM-DD')}</td>
                <td>${ref ? '<span class="cd-badge cd-badge--danger">환불</span>' : '납부'}</td>
                <td class="ta-r">${ref ? '−' : ''}${Number(r.amount).toLocaleString()}원</td>
                <td>${this._esc(r.rel_desc)}</td>
                <td>${this._esc(r.manager_name)}</td>
            </tr>`;
        }).join('');

        const refund = d.refundCnt ? ` · 환불 <b>${d.refundCnt}</b>건` : '';

        return `<div class="cd-sum">납부 <b>${d.paidCnt}</b>건${refund}`
             + ` · 순납부 <b>${Number(d.total).toLocaleString()}</b>원</div>`
             + `<table class="cd-tbl">
                  <thead><tr><th>일자</th><th>구분</th><th class="ta-r">금액</th><th>내용</th><th>처리자</th></tr></thead>
                  <tbody>${rows}</tbody>
                </table>
                <div class="cd-note">어느 장비 건인지는 기록에 남아 있지 않습니다 — 내용란의 메모로만 확인됩니다.</div>`;
    }

    /* ───────────────── 조작 ───────────────── */

    /** 본문은 매번 다시 그리므로 루트에 위임한다 */
    /**
     * 좌우 폭 조절 — 손잡이(.cd-split)를 끌면 목록 폭(`--cd-side-w`)이 바뀐다.
     *
     * 왜 필요했나: 목록 폭이 500px 고정이라 글자를 키우면 **오른쪽(상세)만 빠듯해졌다.**
     * 목록은 이름 몇 줄이면 되는데 남는 자리를 넘겨줄 방법이 없었다.
     *
     * · 값은 **로컬 px** 로 다룬다. `offsetWidth`·`clientWidth` 가 로컬이고, 마우스 좌표(clientX)는
     *   **화면 px** 이라 배율로 나눠 맞춘다 — 안 나누면 커서보다 덜 움직여 잡은 곳이 어긋난다.
     * · 양쪽 최소폭을 지킨다(목록 260 · 상세 360). 한쪽이 0이 되면 되돌릴 손잡이도 사라진다.
     * · 더블클릭하면 기본값으로 — **px 을 계산해 넣지 않고 변수를 지운다.** 그래야 CSS 기본값
     *   (5:5, `var(--cd-side-w, 50%)`)으로 돌아가 창 폭이 바뀌어도 반씩 유지된다.
     *   화살표키로는 20px 씩 움직인다 — 트랙패드로 미세조정이 어렵다.
     * · 고른 값은 기기에 남는다. 창 크기가 바뀌어도 다음에 열 때 그대로다.
     */
    bindSplit($m) {
        const $split = $m.find('#cdSplit');
        // ⚠ $m(this.$root)이 곧 #cdModal 이다 — find() 는 자손만 보므로 자기 자신은 못 찾는다.
        const $wrap  = $m.is('#cdModal') ? $m : $m.find('#cdModal');
        if (!$split.length || !$wrap.length) return;

        const MIN_SIDE = 260, MIN_MAIN = 360;
        const clamp = (px) => {
            const total = $wrap[0].clientWidth;
            return Math.max(MIN_SIDE, Math.min(px, total - MIN_MAIN));
        };
        const apply = (px, save = true) => {
            const v = clamp(px);
            $wrap[0].style.setProperty('--cd-side-w', v + 'px');
            if (save) $j.Common.Store.local.set(CarDrivers.K_SIDE_W, String(Math.round(v)));
        };

        // 저장값 복원 (없으면 CSS 기본값 그대로 둔다)
        const saved = parseInt($j.Common.Store.local.get(CarDrivers.K_SIDE_W), 10);
        if (saved > 0) apply(saved, false);

        const zoom = () => $j.F.Admin.Util.zoomScale();   // --adm-zoom (보통이면 1)
        let startX = 0, startW = 0;
        const onMove = (e) => apply(startW + (e.clientX - startX) / zoom());
        const onUp = () => {
            $(document).off('mousemove.cdsplit mouseup.cdsplit');
            $split.removeClass('is-dragging');
            $('body').removeClass('cd-resizing');
        };
        $split.on('mousedown.cd', (e) => {
            e.preventDefault();
            startX = e.clientX;
            startW = $m.find('.cd-side')[0].offsetWidth;   // 로컬 px (rect 는 화면 px 이다)
            $split.addClass('is-dragging');
            $('body').addClass('cd-resizing');
            $(document).on('mousemove.cdsplit', onMove).on('mouseup.cdsplit', onUp);
        });
        $split.on('dblclick.cd', () => {          // 기본(5:5)로 — 변수를 지워 CSS 기본값에 맡긴다
            $wrap[0].style.removeProperty('--cd-side-w');
            $j.Common.Store.local.remove(CarDrivers.K_SIDE_W);
        });
        $split.on('keydown.cd', (e) => {
            const d = e.key === 'ArrowLeft' ? -20 : e.key === 'ArrowRight' ? 20 : 0;
            if (!d) return;
            e.preventDefault();
            apply($m.find('.cd-side')[0].offsetWidth + d);
        });
    }

    bind() {
        const $m = this.$root.off('.cd');
        this.bindSplit($m);

        // ⚠ 좌측 목록 탭도 `.cd-chip` 이다 — `[data-view]` 로 좁히지 않으면 여기서 함께 잡혀
        //    `undefined` 로 본문을 바꾸려 든다.
        $m.on('click.cd',  '.cd-chip[data-view]', (e) => {
            const $b = $(e.currentTarget);
            // 축은 그린 쪽이 data-axis 로 알려준다 — 이름으로 추측하지 않는다(drawHead 주석 참고).
            $b.data('axis') === 'owner' ? this.goOwner($b.data('view')) : this.goTargetView($b.data('view'));
        });
        $m.on('click.cd',  '.cd-item[data-kind]', (e) => {
            const $b = $(e.currentTarget);
            this.goTarget($b.data('kind'), $b.data('id'));
        });

        $m.on('change.cd', '.cd-driver',   (e) => this.onChangeDriver($(e.currentTarget)));
        $m.on('change.cd', '.cd-car-of',   (e) => this.onChangeCar($(e.currentTarget)));
        $m.on('click.cd',  '.cd-sell',     (e) => this.onRetire($(e.currentTarget), 'sell'));
        $m.on('click.cd',  '.cd-delete',   (e) => this.onRetire($(e.currentTarget), 'delete'));
        $m.on('click.cd',  '.cd-restore',  (e) => this.onRestore($(e.currentTarget)));
        $m.on('click.cd',  '.cd-recover',  (e) => this.onRecover($(e.currentTarget)));
        $m.on('click.cd',  '.cd-goto',     (e) => this.onGoto($(e.currentTarget)));
        // 목록 줄 안의 아이콘이기도 하다 — 줄 선택으로 번지지 않게 막는다
        $m.on('click.cd',  '.cd-mobile',   (e) => { e.stopPropagation(); this.openMobile($(e.currentTarget).data('num')); });
        $m.on('click.cd',  '.cd-act',      (e) => this.onAct($(e.currentTarget)));
        $m.on('input.cd',  '#cdHistFind',  (e) => this.onHistFind($(e.currentTarget)));
        $m.on('click.cd',  '#cdCounselAdd',()  => this.onCounselAdd());
        $m.on('click.cd',  '#cdMaxSave',   ()  => this.onSaveMax());
        $m.on('click.cd',  '.cd-chip[data-side]', (e) => this.goSide($(e.currentTarget).data('side')));
        $m.on('click.cd',  '#cdSideAdd',   ()  => (this.sideTab === 'driver' ? this.onAddDriver() : this.onAddCar()));
        $m.on('click.cd',  '#cdDriverSave',()  => this.onSaveDriver());
        $m.on('click.cd',  '#cdParkAddr',  ()  => this.openParkAddress());
        $m.on('click.cd',  '#cdCarSave',   ()  => this.onSaveCar());
        $m.on('click.cd',  '#cdAddSave',   ()  => this.onAddSave());
        $m.on('click.cd',  '#cdAddCancel', ()  => this.goOwner('link'));
        $m.on('click.cd',  '#cdHeadCap',   ()  => this.goOwner('home'));
        $m.on('change.cd', '#cdAddForm [name=cdAddCar], #cdAddFeeOn', () => this.syncAddCar());
        $m.on('input.cd',  '#cdAddFee', (e) => { $j.Common.Form.formatNumberInput(e.currentTarget); this.syncAddCar(); });
        $(window).off('focus.cd').on('focus.cd', () => this.onWindowFocus());
        $m.on('click.cd',  '#cdNewCarSave', () => this.onNewCarSave());
        $m.on('click.cd',  '#cdNewCarSkip', () => this.onNewCarSkip());
        // 차량번호 중복은 칸을 떠날 때 그 자리에서 알린다 (막는 것은 저장 때 서버가 한다)
        $m.on('focusout.cd', '#cdNewCarForm #cd-number', (e) => this.checkPlate($(e.currentTarget).val()));

        // 서류 이동 — 모드 전환·고르기·옮기기
        $m.on('click.cd',  '.cd-docmove-toggle', () => {
            this._docMove = !this._docMove;
            this.drawDocs(this.$root.find('#cdView'));
        });
        $m.on('click.cd',  '.cd-doc--pick', (e) => { $(e.currentTarget).toggleClass('is-picked'); this.syncDocMove(); });
        $m.on('change.cd', '.cd-docmove-type', () => this.syncDocMove());
        $m.on('input.cd',  '.cd-docmove-title', () => this.syncDocMove());
        $m.on('click.cd',  '.cd-docmove-go', () => this.onDocMove());

        // 서류 이미지 — 전체화면 뷰어로 연다 (앞뒤로 넘길 수 있어야 훑어볼 수 있다)
        $m.on('click.cd',  '.cd-doc[data-i]', (e) => {
            const i = Number($(e.currentTarget).data('i'));
            $j.Ui.ZoomPhoto.open(null, {
                items: this._docItems || [],
                index: i,
                // 편집은 서류목록 화면(`/adm/member/doc`)과 **같은 편집창**을 연다.
                // 저장 뒤 어느 서류에 돌려놓을지는 그 화면이 전역(`currentEditDocId`)으로 받는다 —
                // 규약을 여기서 새로 만들지 않고 그대로 따른다.
                actions: [{
                    label: '편집',
                    onClick: (item) => {
                        if (!item || !item.imgId) { $j.F.Admin.Util.tsalert('error', '편집할 이미지가 없습니다.'); return; }
                        G5['currentEditDocId'] = item.docNum;
                        $j.Ui.ZoomPhoto.close();
                        $j.F.ImageEditor.open(item.imgId);
                    },
                }],
            });
        });
    }

    /**
     * 기사 액션 — 편집창의 같은 버튼을 그대로 부른다.
     * `$j.p.act` 는 이 모달을 띄운 기사편집 페이지 모듈의 것이다. 없는 경우(다른 화면에서
     * 이 모달을 열게 되면)를 대비해 확인하고 알린다 — 아무 반응 없는 버튼을 두지 않는다.
     */
    onAct($btn) {
        const a   = $j.p && $j.p.act;
        const num = $btn.closest('.cd-acts').data('num');
        if (!a) { $j.F.Admin.Util.tsalert('error', '이 화면에서는 쓸 수 없는 기능입니다.'); return; }

        switch ($btn.data('act')) {
            case 'push':  a.pushTest(num, this.driverName(num)); break;
            case 'diag':  a.appDiag(num);     break;
            case 'llog':  a.loginLogs(num);   break;
        }
    }

    /**
     * 장비 쪽에서 담당기사를 바꾼다 (연결관리 표 · 장비정보) — 빈 값이면 해제.
     *
     * 다른 기사로 바꿀 때는 **해제하고 연결한다.** 서버 `connect` 는 이미 누가 모는 장비를
     * 거절하므로("먼저 해제해주세요") 한 번에 보내면 갈아태우기가 되지 않는다.
     */
    async onChangeDriver($sel) {
        const num   = $sel.data('num');
        const mbNo  = $sel.val();
        const cur   = String($sel.data('cur') || '');
        const plate = this.carName(num);

        if (String(mbNo) === cur) return;

        const ok = !mbNo
            ? await $j.Ui.Dialog.confirm(`[${plate}] 의 기사배정을 해제할까요?`,
                { desc: '이 장비는 매칭에서 빠집니다.', hint: this.dirtyHint($sel), confirm: '해제' })
            : await $j.Ui.Dialog.confirm(`[${plate}] 을(를) ${this.driverName(mbNo)} 기사에게 배정할까요?`,
                { desc: cur ? `지금 담당인 ${this.driverName(cur)} 기사는 미배정이 됩니다.` : '',
                  hint: this.dirtyHint($sel), confirm: '연결' });
        if (!ok) { $sel.val(cur); return; }

        const steps = [];
        if (cur)  steps.push(['disconnect', { num }]);
        if (mbNo) steps.push(['connect',    { num, mb_no: mbNo }]);
        this.relink(steps, mbNo ? '연결되었습니다.' : '해제되었습니다.');
    }

    /** 기사 쪽에서 배정장비를 바꾼다 (기사정보) — 빈 값이면 해제. 타던 장비는 미배정으로 남는다 */
    async onChangeCar($sel) {
        const mbNo = $sel.data('mb');
        const num  = $sel.val();
        const cur  = String($sel.data('cur') || '');
        const who  = this.driverName(mbNo);

        if (String(num) === cur) return;

        const ok = !num
            ? await $j.Ui.Dialog.confirm(`${who} 기사의 장비배정을 해제할까요?`,
                { desc: `[${this.carName(cur)}] 은(는) 미배정이 되어 매칭에서 빠집니다.`,
                  hint: this.dirtyHint($sel), confirm: '해제' })
            : await $j.Ui.Dialog.confirm(`${who} 기사에게 [${this.carName(num)}] 을(를) 배정할까요?`,
                { desc: cur ? `지금 몰던 [${this.carName(cur)}] 은(는) 미배정이 되어 매칭에서 빠집니다.` : '',
                  hint: this.dirtyHint($sel), confirm: '연결' });
        if (!ok) { $sel.val(cur); return; }

        const steps = [];
        if (cur) steps.push(['disconnect', { num: cur }]);
        if (num) steps.push(['connect',    { num, mb_no: mbNo }]);
        this.relink(steps, num ? '연결되었습니다.' : '해제되었습니다.');
    }

    /**
     * 배정을 바꾸면 목록을 다시 받아 본문을 새로 그린다 — 편집폼 안에서 바꿨다면
     * 저장하지 않은 입력이 함께 사라지므로 확인창에서 미리 알린다. (연결관리 표는 해당 없다)
     */
    dirtyHint($sel) {
        return $sel.closest('#cdDriverForm, #cdCarForm').length
            ? '이 화면에서 저장하지 않은 입력은 지워집니다.' : '';
    }

    /**
     * 연결·해제를 차례로 보낸다. 판정은 서버가 정본이라(기사당 1대 등) 중간에 거절되면
     * 사유를 보여주고 멈춘다 — 어느 쪽이든 끝나면 목록을 다시 받아 화면을 실제 상태에 맞춘다.
     */
    async relink(steps, okMsg) {
        let fail = null;
        for (const [act, data] of steps) {
            const r = await this.send(`/adm/ajax/${this.owner}/car/${act}`, data);
            if (!r.result) { fail = r.message || '처리하지 못했습니다.'; break; }
        }

        if (fail) await $j.Ui.Dialog.alert(fail);
        else $j.F.Admin.Util.tsalert('success', okMsg, 'bottom-right');

        this.load();
        if ($j.p && $j.p.loadCar) $j.p.loadCar();   // 본창 장비요약 갱신
    }

    async onRetire($btn, kind) {
        const num    = $btn.data('num');
        const plate  = this.carName(num);
        const isSell = kind === 'sell';

        const ok = await $j.Ui.Dialog.open({
            tone:    'danger',
            title:   isSell ? `[${plate}] 을(를) 매각 처리할까요?` : `[${plate}] 을(를) 삭제할까요?`,
            desc:    isSell ? '판 장비로 기록합니다. 기사배정이 풀리고 매칭에서 빠집니다.'
                            : '잘못 등록한 장비를 지웁니다. 기사배정이 풀리고 매칭에서 빠집니다.',
            hint:    '왼쪽 목록 아래에 남아 언제든 복구할 수 있습니다.',
            confirm: isSell ? '매각' : '삭제',
            cancel:  '취소',
        });
        if (!ok) return;

        // 지금 그 장비를 보고 있었다면 목록으로 돌려놓는다 — 사라진 대상의 폼이 남으면 헷갈린다
        if (this.sel.kind === 'car' && String(this.sel.id) === String(num)) {
            this.sel = { kind: null, id: 0 };
            this.view = 'link';
        }

        const r = isSell
            ? await this.post(`/adm/ajax/${this.owner}/car/sell`, { num }, '매각 처리했습니다.')
            : await this.post(`/adm/ajax/${this.owner}/car`, { num }, '삭제했습니다.', null, 'DELETE');

        // 마지막 장비였는지는 서버가 판정해서 알려준다 (CarService::retire → ask_baljooja)
        if (r && r.ask_baljooja) await this.askBaljooja();
    }

    /**
     * 장비가 0 대가 됐을 때 — 발주자로 되돌릴지 묻는다.
     *
     * 자동으로 바꾸지 않는 이유는 잘못 지웠다 복구하는 일이 흔해서다. 여기서 "그대로 두기" 를
     * 골라도 나중에 기사편집창에서 언제든 바꿀 수 있다.
     */
    async askBaljooja() {
        const ok = await $j.Ui.Dialog.open({
            tone:    'warn',
            title:   '사용중인 장비가 없습니다.',
            desc:    '이 회원을 발주자로 바꿀까요?',
            notes:   ['수주 매칭에서 빠지고 발주만 할 수 있게 됩니다.',
                      '차수·캐시·소속자는 그대로 둡니다.'],
            hint:    '장비를 다시 등록하거나 복구하면 기사편집창에서 수발주자로 되돌릴 수 있습니다.',
            confirm: '발주자로 변경',
            cancel:  '그대로 두기',
        });
        if (!ok) return;

        await this.post(`/adm/ajax/member/${this.owner}/to-baljooja`, {}, '발주자로 변경했습니다.');
    }

    async onRestore($btn) {
        const num = $btn.data('num');
        const ok  = await $j.Ui.Dialog.confirm(`[${this.carName(num)}] 을(를) 복구할까요?`,
                      { desc: '사용중으로 되돌립니다. 기사배정은 다시 지정해야 합니다.', confirm: '복구' });
        if (!ok) return;

        this.post(`/adm/ajax/${this.owner}/car/restore`, { num }, '복구했습니다.');
    }

    /**
     * 탈퇴회원 복구 — 편집창 하단의 [복구]와 **같은 엔드포인트**다 (`Actions.recover`).
     *
     * 그 메서드를 그대로 못 부르는 것은 '지금 화면의 회원'(=소유자)을 보고 있어서다.
     * 여기서 되돌리는 것은 왼쪽에서 고른 소속자다.
     *
     * ⚠ 되돌리는 것은 **회원상태(mem_status)뿐**이다. 이 업체에서의 퇴사표시
     *   (`g5_member_drivers.status=9`)는 그대로라 복구해도 [퇴사자] 묶음에 남는다.
     *   축이 다르다 — 회원을 되살리는 것과 이 업체에 다시 넣는 것은 같은 일이 아니다.
     */
    async onRecover($btn) {
        const mbNo = $btn.data('num');
        const ok   = await $j.Ui.Dialog.confirm(`[${this.driverName(mbNo)}] 기사의 탈퇴를 취소할까요?`,
                       { desc: '서비스상태를 정상으로 되돌립니다. 그 뒤에 정보를 고칠 수 있습니다.',
                         hint: '이 업체의 퇴사표시는 그대로라 [퇴사자] 묶음에 남습니다.',
                         confirm: '복구' });
        if (!ok) return;

        await this.post(`/adm/ajax/member/${mbNo}/rollback`, {}, '복구했습니다.');
    }

    /* ── 저장 ── */

    /**
     * 기사 저장 — 이 폼이 그린 필드만 보낸다.
     * `saveV2` 가 나머지를 DB 값으로 채우므로(fillMissing) 부분 전송이 안전하다.
     */
    onSaveDriver() {
        const $f = this.$root.find('#cdDriverForm');

        // 검증은 본창과 **같은 것**을 쓴다 ($j.p.save.validate). 여기서 규칙을 새로 쓰면
        // 같은 값이 한쪽에서만 걸리는 화면이 된다 — 생년월일 실재성·비번 길이·차고지 GPS 짝까지
        // 전부 그쪽에 있다. 폼 범위와 편집모드만 넘긴다.
        if ($j.p && $j.p.save && !$j.p.save.validate($f, 'edit')) return;

        const name = String($f.find('[name=mem_name]').val() || '').trim();

        // 폼에 있는 것만 담는다. 없는 키는 서버가 DB 값으로 채운다(= 변경 없음).
        const data = {
            editMode:      'edit',
            editnumber:    $f.data('num'),
            mem_ownercode: this.owner,
            mem_name:      name,
        };
        $f.find('[name]').each((_, el) => {
            const $el = $(el), n = $el.attr('name');
            if (n === 'mem_name') return;
            // 안 켜진 라디오·체크박스는 담지 않는다. 체크박스를 담으면 꺼져 있어도 val()이 '1' 이라
            // 앞의 hidden 0 을 덮어써 **끄기가 반영되지 않는다.**
            if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
            data[n] = $el.val();
        });

        // 비밀번호는 비워두면 아예 보내지 않는다 — 서버가 4자 미만을 무시하긴 하지만,
        // 안 보내는 편이 '건드리지 않았다'는 뜻에 더 맞다.
        if (!String(data.mem_pwd || '').trim()) delete data.mem_pwd;

        this.post('/adm/ajax/member/v2/save', data, '기사정보를 저장했습니다.');
    }

    /** 장비 저장 — `num` 을 실어보내야 기사 미배정 장비도 대상이 된다 */
    onSaveCar() {
        const data = this.carFormData('');   // 배정은 연결관리가 다룬다
        if (!data) return;
        data.num = this.$root.find('#cdCarForm').data('num');

        this.post(`/adm/ajax/${this.owner}/car/apply`, data, '장비정보를 저장했습니다.');
    }

    onSaveMax() {
        const cnt = parseInt(this.$root.find('#cdMax').val(), 10);
        if (isNaN(cnt)) { $j.Ui.Dialog.alert('최대대수를 숫자로 입력해주세요.'); return; }

        this.post(`/adm/ajax/${this.owner}/car/max-cnt`, { cnt }, '최대 장비대수를 변경했습니다.');
    }

    /* ── 추가 · 이동 ── */

    /**
     * 장비 추가 — 오른쪽 칸에서 받는다 (`newCarView`). 예전엔 편집창의 장비추가 모달을 겹쳐 띄웠는데,
     * 그 모달은 편집창 페이지 모듈의 것이라 기사관리 목록에서 이 모달을 열면 띄울 수 없었다.
     */
    onAddCar() {
        this.goNewCar(0);
    }

    /** 소속자 추가 — 회원가입 폼이라 모달에 담지 않고 기존 화면으로 넘긴다 */
    /**
     * 소속자 추가 — **화면을 옮기지 않고** 오른쪽에서 받는다.
     *
     * 예전엔 편집창의 추가화면(`?act=add`)으로 넘겼는데, 그러면 지금 모달에서 하던 일이
     * 통째로 날아가서 확인창부터 띄워야 했다. 소속자 하나 만드는 데 필요한 값은 몇 개뿐이라
     * 여기서 받고, 나머지(서류·제재·기사타입)는 만든 뒤 편집창에서 다룬다.
     *
     * 아이디는 화면에서 만들 수 없다 — **소유자 핸드폰 + 순번**이고 이미 쓰는 번호를 건너뛰며
     * 비는 자리를 찾아야 한다(`MemberNamingService::empId`). 서버에서 받아 읽기전용으로 보여준다.
     */
    onAddDriver() {
        this.leaveCounsel('add');
        this.sel  = { kind: null, id: 0 };
        this.view = 'add';
        this.after();
    }

    /**
     * 기사 폰 화면을 연다 (대리로그인) — 기사관리 목록의 '모바일 알림' 과 같은 창이다.
     *
     * **소유자의 서류를 보는 중이면 모바일 서류등록 화면으로 간다.** 서류를 보다 폰 화면을 여는 것은
     * 대개 빠진 서류를 대신 올리려는 것이다. 소유자만인 이유는 목록 메뉴의 '모바일 서류보기' 가
     * 소유자 전용이어서다(Pop.ACTIONS).
     */
    openMobile(num) {
        const isOwner  = String(num) === String(this.owner);
        const ownerDoc = this.view === 'docs' && (!this.sel.kind || this.isOwnerSel());

        if (isOwner && ownerDoc) return $j.F.Admin.Util.userDocs(num);
        $j.F.Admin.Util.userOrder(num);
    }

    /** 이 폼이 안 담는 것(기사타입·서류·제재 등)을 고쳐야 할 때 — 편집창을 새 창으로 */
    onGoto($btn) {
        $j.F.Admin.Util.memberEditSub($btn.data('num'));
    }

    /* ───────────────── 공통 ───────────────── */

    /**
     * 여러 요청을 차례로 잇는 흐름용 — 알림·재조회 없이 응답만 돌려준다 (`post` 는 매번 목록을 다시 받는다).
     * 통신이 실패해도 던지지 않고 `{result:false}` 로 맞춘다.
     */
    send(url, data) {
        return new Promise((resolve) => {
            $.post(url, data)
                .done((r) => resolve(r || { result: false }))
                .fail((xhr) => resolve({ result: false, message: `처리 중 오류가 발생했습니다. (${xhr.status})` }));
        });
    }

    /**
     * 서버가 거절하면(result:false) 사유를 보여주고 화면을 되돌린다.
     * 연결 규칙(기사당 1대 등)의 판정은 서버가 정본이라 성공을 가정하지 않는다.
     */
    post(url, data, okMsg, onFail, method = 'POST') {
        return new Promise((resolve) => {
            $.ajax({ type: method, url, data })
                .done((r) => {
                    if (r && r.result === false) {
                        $j.Ui.Dialog.alert(r.message || '처리하지 못했습니다.');
                        if (onFail) onFail(); else this.load();
                        resolve(null);
                        return;
                    }
                    $j.F.Admin.Util.tsalert('success', okMsg, 'bottom-right');
                    this.load();
                    if ($j.p && $j.p.loadCar) $j.p.loadCar();   // 본창 장비요약 갱신
                    resolve(r || {});
                })
                .fail((xhr) => {
                    $j.Ui.Dialog.alert(`처리 중 오류가 발생했습니다. (${xhr.status})`);
                    if (onFail) onFail();
                    resolve(null);
                });
        });
    }

    carName(num) {
        const c = this.data.cars.find((x) => String(x.num) === String(num));
        return (c && c.car_platenumber) || '번호없음';
    }

    driverName(mbNo) {
        const d = this.data.drivers.find((x) => String(x.num) === String(mbNo));
        return (d && d.mem_name) || '';
    }

    /** 사람이 넣은 값(이름·차량번호·메모)을 그대로 HTML 에 넣지 않는다 */
    _esc(v) {
        return $('<i>').text(v ?? '').html();
    }
}
